import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import moment from "moment";
import { empty, Observable } from "rxjs";
import { concatMap, expand, map, shareReplay, toArray } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { DeepPartial } from "../utils/capex.service";
import {
  IdNameMixin,
  KPI,
  QuotePartCreatePayload,
  QuotePartUpdatePayload,
  QuotePartItem,
  RequireAtLeastOne,
  QuotePartPermission,
  EnableValue,
  PerimeterTypes,
  QuotePartIds,
  QuotePartCreateResponse,
  QuotePartDeleteResponse,
  QuotePartUpdateResponse,
  AssociatedPerimeters,
  RoadmapInventaire
} from "./models";
import { marker as _ } from "@colsen1991/ngx-translate-extract-marker";
import { ErrorService } from "../utils";
import { AutoPPAInput } from "../utils/auto-ppa.service";

/**
 * Structure of a paginated API response.
 */
interface Pagination<T> {
  count: number;
  next: string;
  previous: string;
  results: T[];
}

interface BudgetPlanParams {
  years?: number[];
  perimeters?: number[];
  audits?: number[];
}

interface NotationParams {
  start_year: number;
  end_year: number;
  buildings?: string;
  questions?: string;
  ownership_type?: number;
  scenario?: number;
  depth?: number;
  include_hypothesis?: number;
}

interface RoadmapNotationParams {
  year: number;
  buildings?: string;
}

declare global {
  interface UserID extends Number {
    JUST_MARK_AS_UNKNOWN: any;
  }
}

export type AssetListFilter = {
  fields: string; // id,label,asset_type, etc
  buildings: string; // 1,2,3,4
  ids: string; // 1,2,3,4
  end_of_life: number;
  is_eligible_auto_ppa: boolean;
}

@Injectable()
export class APIService {
  constructor(private http: HttpClient, private errorService: ErrorService) { }

  public get_available_report_templates(params?: any): Observable<KPI.ReportTemplate[]> {
    return this.makeRequest<KPI.ReportTemplate[]>(`/reporting/report-templates/`, "GET", null, params);
  }

  /**
   * @deprecated
   * use get_settings
   */
  public get_decision_settings(): Observable<KPI.DecisionSettings> {
    return this.makeRequest<KPI.DecisionSettings>(`/decision/api/decision-settings/`, "GET");
  }

  public get_settings(): Observable<KPI.Setting[]> {
    return this.makeRequest<KPI.Setting[]>(`/config/api/settings/`, "GET");
  }

  public assets_table(options: any): Observable<any> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<KPI.Asset>(`/decision/api/assets/`, "POST", options, null, headers);
  }

  public assets_equipment_table<T extends any>(options: any) {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<{ lastRow: number, rowCount: number, rowData: T[], totalBudget: number }>(
      `/decision/api/equipment/`,
      'POST',
      options,
      null,
      headers
    )
  }

  public postponements_table(options) {
    return this.makeRequest<KPI.InvestmentPostponementRowsData[]>(
      `/decision/api/postponements/`,
      "POST",
      options,
      null
    );
  }

  /**
   * Before going to download the asset table, we should see what
   * will be contain
   * @param options
   * @returns
   */
  public pre_download_assets_excel_export(options: KPI.TExportPayload) {
    let { payload, columnDefs } = options;
    const requestOptions = {
      columnDefs,
      ...payload
    };
    const endpoint = `/decision/api/assets/pre_export/`;

    return this.makeRequest<KPI.TPreExportResp>(endpoint, "POST", requestOptions, null);
  }

  /**
   * Before going to download the asset table, we should see what
   * will be contain
   * @param options
   * @returns
   */
  public pre_download_investments_excel_export(options: KPI.TExportPayload) {
    let { payload, columnDefs } = options;
    const requestOptions = {
      columnDefs,
      ...payload
    };
    const endpoint = `/decision/api/investments/pre_export/`;

    return this.makeRequest<KPI.TPreExportResp>(endpoint, "POST", requestOptions, null);
  }

  /**
   * Before going to download the initiative table, we should see what
   * will be contain
   * @param options
   * @returns
   */
  public pre_download_initiatives_excel_export(options: KPI.TExportPayload) {
    let { payload, columnDefs } = options;
    const requestOptions = {
      columnDefs,
      ...payload
    };
    const endpoint = `/decision/api/initiatives/pre_export/`;
    return this.makeRequest<KPI.TPreExportResp>(endpoint, "POST", requestOptions, null);
  }

  public initiatives_table(options: any): Observable<any> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<any>(`/decision/api/initiatives/`, "POST", options, null, headers);
  }

  public post_fidji_act_import(responsible: string, import_file: File): Observable<any> {
    const body = new FormData();
    body.append("responsible", responsible);
    body.append("import_file", import_file);
    return this.makeRequest<any>(`/fidji-act/api/import/`, "POST", body, null, new HttpHeaders(), "blob");
  }

  public get_fidji_act_transposed(siteIds: number[] = []): Observable<KPI.FidjiActTransposed> {
    return this.makeRequest<KPI.FidjiActTransposed>(`/fidji-act/api/transposed/`, "POST", { site_ids: siteIds });
  }

  public set_fidji_act_non_applicable(params: {
    initiative_types: number[];
    non_applicable: boolean;
    mono_perimeter: number;
  }): Observable<any> {
    return this.makeRequest<any>(`/fidji-act/api/non-applicable/`, "PUT", params);
  }

  public set_fidji_act_already_done(params: {
    initiative_types: number[];
    already_done: boolean;
    mono_perimeter: number;
  }): Observable<any> {
    return this.makeRequest<any>(`/fidji-act/api/already-done/`, "PUT", params);
  }

  public set_fidji_act_to_investigate(params: {
    initiative_types: number[];
    to_investigate: boolean;
    mono_perimeter: number;
  }): Observable<any> {
    return this.makeRequest<any>(`/fidji-act/api/to-investigate/`, "PUT", params);
  }

  public set_fidji_act_priority(params: {
    initiative_types: number[];
    is_active: boolean;
  }): Observable<any> {
    return this.makeRequest<any>(`/fidji-act/api/priority/`, "PUT", params);
  }

  public fidji_act_create_task(task: Partial<KPI.FidjiActTask>): Observable<KPI.FidjiActTask> {
    return this.makeRequest<KPI.FidjiActTask>(`/fidji-act/api/tasks/`, "POST", task);
  }

  public fidji_act_list_tasks(): Observable<KPI.FidjiActTask[]> {
    return this.makeRequest<KPI.FidjiActTask[]>(`/fidji-act/api/tasks/list/`, "GET");
  }

  public fidji_act_export_tasks(): Observable<Blob> {
    return this.makeRequest<Blob>(`/fidji-act/api/tasks/export/`, "GET", null, null, new HttpHeaders(), "blob");
  }

  public fidji_act_update_task_status(taskId: number, state: number): Observable<{ id: number; state: number; state_display: string }> {
    return this.makeRequest(`/fidji-act/api/tasks/${taskId}/status/`, "POST", { state });
  }

  public fidji_act_update_task(
    taskId: number,
    task: Partial<Pick<KPI.FidjiActTask, "label" | "assigned_email" | "deadline">> & {
      document?: File | null;
      remove_document?: boolean;
    },
  ): Observable<
    Pick<KPI.FidjiActTask, "id" | "label" | "assigned_email" | "deadline" | "state" | "state_display" | "document_url" | "document_name">
  > {
    const formData = new FormData();
    if (task.label !== undefined) formData.append("label", task.label);
    if (task.assigned_email !== undefined) formData.append("assigned_email", task.assigned_email);
    if (task.deadline !== undefined) formData.append("deadline", task.deadline);
    if (task.document) formData.append("document", task.document);
    if (task.remove_document) formData.append("remove_document", "true");
    return this.makeRequest(`/fidji-act/api/tasks/${taskId}/update/`, "PATCH", formData);
  }

  public fidji_act_delete_task(taskId: number): Observable<void> {
    return this.makeRequest(`/fidji-act/api/tasks/${taskId}/`, "DELETE");
  }

  public fidji_act_save_comment(params: { initiative_type: number; mono_perimeter: number; text: string }): Observable<any> {
    return this.makeRequest<any>(`/fidji-act/api/comment/`, "PUT", params);
  }

  public fidji_act_get_fiche_notice(initiativeTypeId: number): Observable<KPI.FidjiActFicheNotice> {
    return this.makeRequest<KPI.FidjiActFicheNotice>(
      `/fidji-act/api/fiche-notice/initiative-type/${initiativeTypeId}/`,
      "GET",
    );
  }

  public meeting_report_get_report(perimeterId: number): Observable<KPI.MeetingReport> {
    return this.makeRequest<KPI.MeetingReport>(`/meeting-report/api/${perimeterId}/`, "GET");
  }

  public meeting_report_save_content(
    perimeterId: number,
    content: KPI.TiptapDocument,
  ): Observable<KPI.MeetingReport> {
    return this.makeRequest(`/meeting-report/api/${perimeterId}/`, "PUT", { content });
  }

  public meeting_report_list_indicators(): Observable<KPI.MeetingReportIndicator[]> {
    return this.makeRequest<KPI.MeetingReportIndicator[]>(`/meeting-report/api/indicators/`, "GET");
  }

  public fidji_act_save_fiche_notice(params: {
    initiative_type: number;
    comment: string;
    resume: string;
  }): Observable<KPI.FidjiActFicheNotice> {
    return this.makeRequest<KPI.FidjiActFicheNotice>(`/fidji-act/api/fiche-notice/`, "PUT", params);
  }

  public fidji_act_add_fiche_notice_referent(params: {
    initiative_type: number;
    name: string;
    company?: string;
    email?: string;
    order?: number;
  }): Observable<KPI.FidjiActFicheNoticeReferent> {
    return this.makeRequest<KPI.FidjiActFicheNoticeReferent>(`/fidji-act/api/fiche-notice-referent/`, "POST", params);
  }

  public fidji_act_delete_fiche_notice_referent(referentId: number): Observable<void> {
    return this.makeRequest<void>(`/fidji-act/api/fiche-notice-referent/${referentId}/`, "DELETE");
  }

  public projects_table(options: any): Observable<any> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<any>(`/decision/api/projects/`, "POST", options, null, headers);
  }

  public investments_table(options: any): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.makeRequest<KPI.Investment>(
      `/decision/api/investments/`,
      'POST',
      options,
      null,
      headers,
    );
  }

  public sites_table(options: any): Observable<any> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<any>(`/decision/api/sites-table/`, "POST", options, null, headers);
  }

  public administration_sites_table(options: any): Observable<any> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<any>(`/decision/api/administration/sites-table/`, "POST", options, null, headers);
  }

  public perimeters_table(options: any): Observable<any> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<any>(`/decision/api/perimeters-table/`, "POST", options, null, headers);
  }

  public available_filter_values(options: any): Observable<any> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<any>(`/decision/api/filter-values/`, "POST", options, null, headers);
  }

  public get_capex_filtered(options: any): Observable<KPI.SimpleCapex> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<any>(`/decision/api/capex-filtered/`, "POST", options, null, headers);
  }

  public get_assets_by_state(options: any): Observable<KPI.AssetsByYearAndNote> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<KPI.AssetsByYearAndNote>(`/decision/api/assets-by-state/`, "POST", options, null, headers);
  }

  public get_assets_renewals(options: any): Observable<KPI.AssetsRenewalsChartData> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<KPI.AssetsRenewalsChartData>(
      `/decision/api/assets-renewals/`,
      "POST",
      options,
      null,
      headers
    );
  }

  public get_assets_total_by_filter(options: any): Observable<any> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<any>(`/decision/api/assets-total-by-filter/`, "POST", options, null, headers);
  }

  public get_investments_by_priority(options: any): Observable<any> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<any>(`/decision/api/investments-by-priority/`, "POST", options, null, headers);
  }

  public get_kpi_expert_chart(options: any): Observable<KPI.KPIExpertChart> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<KPI.KPIExpertChart>(`/audit-expert/api/kpi-expert-chart/`, "POST", options, null, headers);
  }

  public get_ghg_absolute_emission_avoided(options: any): Observable<KPI.GHGEmissionsChart> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<KPI.GHGEmissionsChart>(
      `/initiatives/api/ghg-absolute-emission-avoided/`,
      "POST",
      options,
      null,
      headers
    );
  }

  public get_scenario_initiative_comparison(options: any): Observable<KPI.ScenarioInitiativeComparison> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<KPI.ScenarioInitiativeComparison>(
      `/initiatives/api/scenario-initiative-comparison/`,
      "POST",
      options,
      null,
      headers
    );
  }

  public get_european_taxonomy_eligibility(options?: any): Observable<KPI.TotalInvestmentsByTaxonomyEligibility> {
    return this.makeRequest<KPI.TotalInvestmentsByTaxonomyEligibility>(
      `/investments/api/investment-eligible-taxonomy/`,
      "GET",
      null,
      options
    );
  }

  public get_eligible_by_taxonomy_category(options?: any): Observable<KPI.EligibilityByTaxonomyCat> {
    return this.makeRequest<KPI.EligibilityByTaxonomyCat>(
      `/investments/api/investment-eligible-taxonomy/category/`,
      "GET",
      null,
      options
    );
  }

  public get_budget_by_taxonomy_objective(options?: any): Observable<KPI.TaxonomyObjectiveBudget[]> {
    return this.makeRequest<KPI.TaxonomyObjectiveBudget[]>(
      `/investments/api/budget-by-taxonomy-objective/`,
      "GET",
      null,
      options
    );
  }

  public get_taxonomy_objectives(params?: any, waitAllPages: boolean = false): Observable<KPI.TaxonomyObjective[]> {
    return this.followPagination<KPI.TaxonomyObjective>("/investments/api/taxonomy-objectives/", params, waitAllPages);
  }

  public get_taxonomy_categories(params?: any, waitAllPages: boolean = false): Observable<KPI.TaxonomyCategory[]> {
    return this.followPagination<KPI.TaxonomyCategory>("/investments/api/taxonomy-categories/", params, waitAllPages);
  }

  public get_heat_transfer_coefficient_u(waitAllPages: boolean = false): Observable<IdNameMixin[]> {
    return this.followPagination<IdNameMixin>("/assets/api/heat-transfer-coefficients/", null, waitAllPages);
  }

  public get_source_info_heat_transfer_coefficient_u(waitAllPages: boolean = false): Observable<IdNameMixin[]> {
    return this.followPagination<IdNameMixin>("/assets/api/source-info-heat-transfer-coefficients", null, waitAllPages);
  }

  public get_thermal_resistance_coefficient_r(waitAllPages: boolean = false): Observable<IdNameMixin[]> {
    return this.followPagination<IdNameMixin>("/assets/api/thermal-resistance-coefficients", null, waitAllPages);
  }

  public get_source_info_thermal_resistance_coefficient_r(waitAllPages: boolean = false): Observable<IdNameMixin[]> {
    return this.followPagination<IdNameMixin>(
      "/assets/api/source-info-thermal-resistance-coefficients",
      null,
      waitAllPages
    );
  }

  public get_budget_scopes(waitAllPages: boolean = false): Observable<KPI.BudgetScope[]> {
    return this.followPagination<KPI.BudgetScope>("/assets/api/budget-scopes", null, waitAllPages);
  }

  public get_confidences_on_feasibility(): Observable<IdNameMixin[]> {
    return this.followPagination<IdNameMixin>("/initiatives/api/confidences-on-feasibility");
  }

  public get_energy_trajectory(options: any): Observable<KPI.EnergyTrajectory> {
    return this.makeRequest<KPI.EnergyTrajectory>(
      `/initiatives/api/energy-consumptions-trajectory/`,
      "GET",
      null,
      options
    );
  }

  public get_initiative_energy_trajectory(
    initiativeId: number,
    options: any
  ): Observable<KPI.InitiativeEnergyTrajectory> {
    return this.makeRequest<KPI.InitiativeEnergyTrajectory>(
      `/initiatives/api/initiatives/${initiativeId}/energy-consumption-trajectory`,
      "GET",
      null,
      options
    );
  }

  public get_initiative_energy_trajectory_for_asset(assetId: number): Observable<KPI.InitiativeEnergyTrajectory> {
    return this.makeRequest<KPI.InitiativeEnergyTrajectory>(
      `/assets/api/assets/${assetId}/detailed-initiative-carbon/`,
      "GET",
      null,
      {}
    );
  }

  public get_initiative_energy_trajectory_for_invest(investmentId: number): Observable<KPI.InitiativeEnergyTrajectory> {
    return this.makeRequest<KPI.InitiativeEnergyTrajectory>(
      `/investments/api/investments/${investmentId}/detailed-initiative-carbon/`,
      "GET",
      null,
      {}
    );
  }

  public get_initiative_energy_trajectory_for_project(
    projectId: number,
    queryParams: any
  ): Observable<KPI.InitiativeEnergyTrajectory> {
    return this.makeRequest<KPI.InitiativeEnergyTrajectory>(
      `/projects/api/projects/${projectId}/energy-consumption-trajectory/`,
      "GET",
      null,
      queryParams
    );
  }

  public get_carbon_trajectory(options: any): Observable<KPI.CarbonTrajectory> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<KPI.CarbonTrajectory>(`/initiatives/api/carbon-emission/`, "POST", options, null, headers);
  }

  public get_stranded_sites(options: any): Observable<KPI.StrandedSites> {
    return this.makeRequest<KPI.StrandedSites>(`/initiatives/api/stranded-sites`, "GET", null, options);
  }

  public get_ghg_intensity_pathway(options: any): Observable<KPI.GHGIntensityPathway[]> {
    return this.makeRequest<KPI.GHGIntensityPathway[]>(`/initiatives/api/ghg-intensity-pathway/`, "GET", null, options);
  }

  public get_energy_intensity_pathway(options: any): Observable<KPI.EnergyIntensityPathway[]> {
    return this.makeRequest<KPI.EnergyIntensityPathway[]>(
      `/initiatives/api/energy-intensity-pathway/`,
      "GET",
      null,
      options
    );
  }

  public get_ppa_by_cluster(options: any): Observable<any> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<any>(`/decision/api/ppa-by-cluster/`, "POST", options, null, headers);
  }

  public assets_assets_by_page<T>(page: number, params?: any, fields?: string): Observable<T> {
    let headers = new HttpHeaders();

    headers = headers.set("Content-Type", "application/x-www-form-urlencoded");
    const param_helper = new URLSearchParams();
    for (const key in params) {
      if (key) {
        param_helper.set(key, params[key]);
      }
    }
    const body = param_helper.toString();
    return this.makeRequest<T>(
      "/assets/api/assets/",
      "POST",
      body,
      {
        size: 30,
        page,
        ...(fields ? { fields } : {})
      },
      headers
    );
  }

  public audit_asset_kpi_expert(asset_id: number): Observable<KPI.Default[]> {
    return this.makeRequest<KPI.Default[]>(`/audit-expert/api/asset-kpi-expert/${asset_id}`, "GET");
  }

  public audit_asset_create(asset: Partial<KPI.Asset>, params?: any): Observable<KPI.Asset> {
    return this.makeRequest<KPI.Asset>(
      `/audit/api/assets/`,
      'POST',
      asset,
      params,
    )
  }

  public assets_assets(
    params?: Partial<AssetListFilter>,
    waitAllPages: boolean = false,
  ): Observable<any> {
    // From a backend point of view, it would be better not to use pagination.
    // It is now possible to give a value up to 50000 to avoid pagination.
    // But getting large number of assets in one call will cause performance issues fo the frontend app.
    // to be investigated.
    const {ids} = params;
    if (ids && ids.length > 5000) console.warn('Try to fech too many assets')
    return this.followPagination<any>("/assets/api/assets/", params, waitAllPages, 50000, true);
  }

  public assets_by_site<T extends keyof KPI.Asset>(siteId: number, fields: T[] = [] as T[], queryParams: {
    [key: string]: string | number | boolean
  } = null): Observable<Pick<KPI.Asset, T>[]> {
    const searchParams = new URLSearchParams();
    searchParams.set('site', siteId.toString());
    if (fields && fields.length > 0) {
      searchParams.set('fields', fields.join(','));
    }
    for (const key in queryParams) {
      if (queryParams.hasOwnProperty(key)) {
        searchParams.set(key, queryParams[key].toString());
      }
    }
    const url = `/assets/api/assets/?${searchParams.toString()}`;
    return this.followPagination<Pick<KPI.Asset, T>>(
      url,
      null,
      false,
      50000,
      false,
    );
  }

  public assets_assets_get(id: number): Observable<KPI.Asset> {
    return this.makeRequest<KPI.Asset>(`/assets/api/assets/${id}/`, "GET");
  }

  public get_asset_lifecycle(id: number): Observable<KPI.AssetLifecycle> {
    return this.makeRequest<KPI.AssetLifecycle>(`/assets/api/assets/${id}/lifecycle`, "GET");
  }

  public assets_assets_patch(id: number, asset: Partial<KPI.Asset>): Observable<KPI.Asset> {
    return this.makeRequest<KPI.Asset>(`/audit/api/assets/${id}/`, "PATCH", asset);
  }

  public assets_asset_delete(id: number): Observable<void> {
    return this.makeRequest<void>(`/assets/api/assets/${id}/`, "DELETE");
  }

  public assets_check_delete(ids: number[]): Observable<any> {
    return this.makeRequest<any>(`/assets/api/assets/check_delete/`, "POST", { ids });
  }

  public assets_asset_history(id: number): Observable<KPI.History> {
    return this.makeRequest<KPI.History>(`/assets/api/assets/${id}/history/`, "GET");
  }

  public assets_assets_pictures(params?: any, waitAllPages: boolean = false): Observable<KPI.AssetPicture[]> {
    return this.followPagination<KPI.AssetPicture>(`/assets/api/asset-pictures/`, params, waitAllPages);
  }

  public assets_create_picture(formData: FormData): Observable<KPI.AssetPicture> {
    return this.makeRequest<KPI.AssetPicture>(`/assets/api/asset-pictures/`, "POST", formData);
  }

  public assets_delete_picture(id: number): Observable<void> {
    return this.makeRequest<void>(`/assets/api/asset-pictures/${id}/`, "DELETE");
  }

  public assets_opex(params?: any): Observable<KPI.AssetOpex[]> {
    return this.followPagination<KPI.AssetOpex>("/assets/api/opex/", params, true);
  }

  public assets_opex_create(data: Partial<KPI.AssetOpex>): Observable<KPI.AssetOpex> {
    return this.makeRequest<KPI.AssetOpex>("/assets/api/opex/", "POST", data);
  }

  public assets_opex_delete(id: number): Observable<void> {
    return this.makeRequest<void>(`/assets/api/opex/${id}/`, "DELETE");
  }

  public asset_types_table(options: any): Observable<KPI.AssetTypesTableItem[]> {
    return this.makeRequest<KPI.AssetTypesTableItem[]>(`/assets/api/asset-types-table/`, "GET", {}, options);
  }

  public assets_asset_types(params?: any, waitAllPages: boolean = false): Observable<KPI.AssetType[]> {
    return this.followPagination<KPI.AssetType>("/assets/api/asset-types/", params, waitAllPages);
  }

  public assets_asset_type_patch(id: number, assetType: Partial<KPI.AssetType>): Observable<KPI.AssetType> {
    return this.makeRequest<KPI.AssetType>(`/assets/api/asset-types/${id}/`, "PATCH", assetType);
  }

  public assets_zones(params?: any, waitAllPages: boolean = false): Observable<KPI.Zone[]> {
    return this.followPagination<KPI.Zone>("/assets/api/zones/", params, waitAllPages);
  }

  public assets_super_categories(params?: any, waitAllPages: boolean = false): Observable<KPI.SuperCategory[]> {
    return this.followPagination<KPI.SuperCategory>("/assets/api/super-categories/", params, waitAllPages);
  }

  public assets_super_category_patch(
    id: number,
    superCategory: Partial<KPI.SuperCategory>
  ): Observable<KPI.SuperCategory> {
    return this.makeRequest<KPI.SuperCategory>(`/assets/api/super-categories/${id}/`, "PATCH", superCategory);
  }

  public assets_categories(params?: any, waitAllPages: boolean = false): Observable<KPI.Category[]> {
    return this.followPagination<KPI.Category>("/assets/api/categories/", params, waitAllPages);
  }

  public assets_category_patch(id: number, category: Partial<KPI.Category>): Observable<KPI.Category> {
    return this.makeRequest<KPI.Category>(`/assets/api/categories/${id}/`, "PATCH", category);
  }

  public assets_sub_categories(params?: any, waitAllPages: boolean = false): Observable<KPI.SubCategory[]> {
    return this.followPagination<KPI.SubCategory>("/assets/api/sub-categories/", params, waitAllPages);
  }

  public assets_sub_category_patch(id: number, subCategory: Partial<KPI.SubCategory>): Observable<KPI.SubCategory> {
    return this.makeRequest<KPI.SubCategory>(`/assets/api/sub-categories/${id}/`, "PATCH", subCategory);
  }

  public assets_ownership_types(params?: any, waitAllPages: boolean = false): Observable<KPI.OwnershipType[]> {
    return this.followPagination<KPI.OwnershipType>("/assets/api/ownership-types/", params, waitAllPages);
  }

  public assets_power_sources(params?: any, waitAllPages: boolean = false): Observable<KPI.AssetPowerSource[]> {
    return this.followPagination<KPI.AssetPowerSource>("/assets/api/asset-power-sources/", params, waitAllPages);
  }

  public assets_quantity_units(params?: any, waitAllPages: boolean = false): Observable<KPI.QuantityUnit[]> {
    return this.followPagination<KPI.QuantityUnit>("/assets/api/quantity-units/", params, waitAllPages);
  }

  public assets_refrigerant_types(params?: any, waitAllPages: boolean = false): Observable<KPI.RefrigerantType[]> {
    return this.followPagination<KPI.RefrigerantType>("/assets/api/refrigerant-types/", params, waitAllPages);
  }

  public audit_asset_notations(params?: any, waitAllPages: boolean = false): Observable<KPI.AssetNotation[]> {
    return this.followPagination<KPI.AssetNotation>("/audit/api/asset-notations/", params, waitAllPages);
  }

  public audit_api_audit_notation_states(params?: any, waitAllPages: boolean = false): Observable<KPI.NotationState[]> {
    return this.followPagination<KPI.NotationState>("/audit/api/audits/notation-states/", params, waitAllPages);
  }

  public audit_notation_post(assetId: number, payload: KPI.AuditNotationPayload, timestamp?: number): Observable<any> {
    const url = timestamp ? `/audit/api/notation/${assetId}/?ts=${timestamp}` : `/audit/api/notation/${assetId}/`;
    return this.makeRequest<any>(url, "POST", payload);
  }

  public assets_floors(params?: any, waitAllPages: boolean = false): Observable<KPI.AssetFloor[]> {
    return this.followPagination<KPI.AssetFloor>("/assets/api/asset-floors/", params, waitAllPages);
  }

  public audit_audits(): Observable<KPI.Audit[]> {
    return this.makeRequest<KPI.Audit[]>("/audit/api/audits/", "GET");
  }

  public audit_audits_patch(auditId: number, audit: Partial<KPI.Audit>): Observable<KPI.Audit> {
    return this.makeRequest<KPI.Audit>(`/audit/api/audits/${auditId}/`, "PATCH", audit);
  }

  public audit_audits_post(audit: Partial<KPI.Audit>): Observable<KPI.Audit> {
    return this.makeRequest<KPI.Audit>("/audit/api/audits/", "POST", audit);
  }

  public audit_synthesis(perimeterId: number, year: number, lastRefresh: number = 0): Observable<any> {
    return this.makeRequest<any>(`/audit/api/synthesis/${perimeterId}/${year}/${lastRefresh}/`, "GET");
  }

  public audit_notations_global(params: NotationParams): Observable<KPI.GlobalComputedNotation> {
    const headers = new HttpHeaders().set("Content-Type", "application/x-www-form-urlencoded");
    const param_helper = new URLSearchParams();
    for (const key in params) {
      if (key) {
        param_helper.set(key, params[key]);
      }
    }
    const body = param_helper.toString();
    return this.makeRequest<KPI.GlobalComputedNotation>("/audit/api/notations/", "POST", body, null, headers);
  }

  public audit_notations_super_categories(
    params: NotationParams,
    waitAllPages: boolean = false
  ): Observable<KPI.SuperCategoryComputedNotation[]> {
    return this.followPagination<KPI.SuperCategoryComputedNotation>(
      `/audit/api/notations/super-categories/`,
      params,
      waitAllPages,
      undefined,
      true
    );
  }

  public audit_notations_categories(
    superCategory: number,
    params: NotationParams,
    waitAllPages: boolean = false
  ): Observable<KPI.CategoryComputedNotation[]> {
    const url = `/audit/api/notations/super-categories/${superCategory}/categories/`;
    return this.followPagination<KPI.CategoryComputedNotation>(url, params, waitAllPages, undefined, true);
  }

  public audit_notations_sub_categories(
    superCategory: number,
    category: number,
    params: NotationParams,
    waitAllPages: boolean = false
  ): Observable<KPI.SubCategoryComputedNotation[]> {
    const url = `/audit/api/notations/super-categories/${superCategory}/categories/${category}/sub-categories/`;
    return this.followPagination<KPI.SubCategoryComputedNotation>(url, params, waitAllPages, undefined, true);
  }

  public audit_notations_global_refresh(categoryId: number): Observable<boolean> {
    return this.makeRequest("/audit/api/cache-reload/", "GET", {}, { category: categoryId });
  }

  public audit_notations_asset_types(
    superCategory: number,
    category: number,
    subCategory: number,
    params: NotationParams,
    waitAllPages: boolean = false
  ): Observable<KPI.AssetTypeComputedNotation[]> {
    const url =
      `/audit/api/notations/super-categories/${superCategory}/categories/${category}/sub-categories/` +
      `${subCategory}/asset-types/`;
    return this.followPagination<KPI.AssetTypeComputedNotation>(url, params, waitAllPages, undefined, true);
  }

  public audit_notations_assets(
    superCategory: number,
    category: number,
    subCategory: number,
    assetType: number,
    params: NotationParams,
    waitAllPages: boolean = false
  ): Observable<KPI.AssetComputedNotation[]> {
    return this.followPagination<KPI.AssetComputedNotation>(
      `/audit/api/notations/super-categories/${superCategory}/categories/${category}/sub-categories/${subCategory}/asset-types/${assetType}/assets/`,
      params,
      waitAllPages,
      undefined,
      true
    );
  }

  public audit_notations_all_assets(
    params: NotationParams,
    waitAllPages: boolean = false
  ): Observable<KPI.AssetComputedNotation[]> {
    return this.followPagination<KPI.AssetComputedNotation>(
      "/audit/api/notations/assets/",
      params,
      waitAllPages,
      undefined,
      true
    );
  }

  public audit_perimeter_budget_plans(
    params?: BudgetPlanParams,
    waitAllPages: boolean = false
  ): Observable<KPI.PerimeterBudgetPlan[]> {
    return this.followPagination<KPI.PerimeterBudgetPlan>("/audit/api/perimeter-budget-plans/", params, waitAllPages);
  }

  public audit_perimeter_budget_plans_patch(
    id: number,
    plan: Partial<KPI.PerimeterBudgetPlan>
  ): Observable<KPI.PerimeterBudgetPlan> {
    return this.makeRequest<KPI.PerimeterBudgetPlan>(`/audit/api/perimeter-budget-plans/${id}/`, "PATCH", plan);
  }

  public audit_perimeter_budget_plans_post(
    plan: Partial<KPI.PerimeterBudgetPlan>
  ): Observable<KPI.PerimeterBudgetPlan> {
    return this.makeRequest<KPI.PerimeterBudgetPlan>("/audit/api/perimeter-budget-plans/", "POST", plan);
  }

  public audit_perimeters(params?: any): Observable<KPI.Perimeter[]> {
    return this.makeRequest<KPI.Perimeter[]>("/decision/api/perimeters/", "GET", null, params);
  }

  /** Every site of the customer, superadmin only (Administration > Access rights > Sites). */
  public administration_perimeters(params?: any): Observable<KPI.Perimeter[]> {
    return this.makeRequest<KPI.Perimeter[]>("/decision/api/administration/perimeters/", "GET", null, params);
  }

  public audit_questions(params?: any, waitAllPages: boolean = false): Observable<KPI.AuditQuestion[]> {
    return this.followPagination<KPI.AuditQuestion>("/audit/api/questions/", params, waitAllPages);
  }

  public audit_rating_reasons(params?: any, waitAllPages: boolean = false): Observable<KPI.RatingReason[]> {
    return this.followPagination<KPI.RatingReason>("/audit/api/rating-reasons/", params, waitAllPages);
  }

  public audit_rating_reasons_for_asset(perimeterId: number, assetId: number): Observable<any[]> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<any>(
      "/audit/api/rating-reasons-for-asset/",
      "POST",
      {
        perimeter_id: perimeterId,
        asset_id: assetId
      },
      null,
      headers
    );
  }

  public config_config(): Observable<KPI.Config> {
    return this.makeRequest<KPI.Config>("/config/api/config/", "GET");
  }

  public costs_cost_centers(): Observable<KPI.CostElement[]> {
    return this.makeRequest<KPI.CostElement[]>("/costs/api/cost-centers/", "GET");
  }

  public costs_costs_spends(params?: any, waitAllPages: boolean = false): Observable<KPI.Spend[]> {
    return this.followPagination<KPI.Spend>("/costs/api/cost-spends/", params, waitAllPages);
  }

  public costs_get_benchmark(
    costElement: KPI.CostElement,
    buildingIds: number[],
    startDate: Date,
    endDate: Date
  ): Observable<KPI.CostBenchmark> {
    const type = costElement.type;
    const id = costElement.id;
    const buildings = buildingIds.join(",");
    const start = moment(startDate).format("YYYY-MM");
    const end = moment(endDate).format("YYYY-MM");

    return this.makeRequest<KPI.CostBenchmark>(
      `/costs/api/benchmark-for-buildings/${type}/${id}/${buildings}/${start}/${end}/`,
      "GET"
    );
  }

  public costs_opex_overview_for_buildings(
    buildingIds: number[],
    startDate: Date,
    endDate: Date
  ): Observable<KPI.OpexOverviewReport> {
    const buildings = buildingIds.join(",");
    const start = moment(startDate).format("YYYY-MM");
    const end = moment(endDate).format("YYYY-MM");

    return this.makeRequest<KPI.OpexOverviewReport>(
      `/costs/api/opex-overview-for-buildings/${buildings}/${start}/${end}/`,
      "GET"
    );
  }

  public documents_documents(params?: any, waitAllPages: boolean = false): Observable<KPI.Document[]> {
    return this.followPagination<KPI.Document>("/documents/api/documents/", params, waitAllPages);
  }

  public documents_download_url(id: number): string {
    return `${environment.backendHost}/documents/api/documents/${id}/download/`;
  }

  public documents_patch(id: number, data: Partial<KPI.Document>): Observable<KPI.Document> {
    return this.makeRequest<KPI.Document>(`/documents/api/documents/${id}/`, "PATCH", data);
  }

  public documents_delete(id: number): Observable<void> {
    return this.makeRequest<void>(
      `/documents/api/documents/${id}/`,
      'DELETE',
    );
  }

  public documents_default_investment_document_types(investmentId: number): Observable<KPI.DocumentRequirement[]> {
    return this.makeRequest<KPI.DocumentRequirement[]>(
      "/documents/api/default-document-types-for-investment/" + investmentId,
      "GET"
    );
  }

  public documents_add_investment_document(investmentId: number, params): Observable<KPI.Document> {
    return this.makeRequest<KPI.Document>(
      "/documents/api/document-types-for-investment-action/" + investmentId,
      "POST",
      params
    );
  }

  public documents_add_perimeter_document(perimeterId: number, params): Observable<KPI.Document> {
    return this.makeRequest<KPI.Document>(
      "/documents/api/document-types-for-perimeter-action/" + perimeterId,
      "POST",
      params
    );
  }

  public documents_add_asset_document(assetId: number, params): Observable<KPI.Document> {
    return this.makeRequest<KPI.Document>(
      "/documents/api/document-types-for-asset-action/" + assetId,
      "POST",
      params
    );
  }

  public documents_add_initiative_document(initiativeId: number, params): Observable<KPI.Document> {
    return this.makeRequest<KPI.Document>(
      "/documents/api/document-types-for-initiative-action/" + initiativeId,
      "POST",
      params
    );
  }

  public documents_requirements_for_perimeter(perimeterId: number): Observable<KPI.DocumentRequirement[]> {
    return this.makeRequest<KPI.DocumentRequirement[]>(
      "/documents/api/document-requirements-for-perimeter/" + perimeterId,
      "GET"
    );
  }

  public documents_document_types(params?: any, waitAllPages: boolean = false): Observable<KPI.DocumentType[]> {
    return this.followPagination<KPI.DocumentType>("/documents/api/document-types/", params, waitAllPages);
  }

  public documents_document_types_delete(id: number): Observable<void> {
    return this.makeRequest<void>(`/documents/api/document-types/${id}/`, "DELETE");
  }

  public documents_document_types_patch(
    id: number,
    documentType: Partial<KPI.DocumentType>
  ): Observable<KPI.DocumentType> {
    return this.makeRequest<KPI.DocumentType>(`/documents/api/document-types/${id}/`, "PATCH", documentType);
  }

  public documents_document_types_post(documentType: Partial<KPI.DocumentType>): Observable<KPI.DocumentType> {
    return this.makeRequest<KPI.DocumentType>(`/documents/api/document-types/`, "POST", documentType);
  }

  public documents_default_asset_document_types(assetId: number): Observable<KPI.DocumentRequirement[]> {
    return this.makeRequest<KPI.DocumentRequirement[]>(`/documents/api/default-document-types-for-asset/${assetId}`, "GET");
  }

  public documents_default_initiative_document_types(initiativeId: number): Observable<KPI.DocumentRequirement[]> {
    return this.makeRequest<KPI.DocumentRequirement[]>(`/documents/api/document-types-for-initiative/${initiativeId}`, "GET");
  }

  public documents_document_type_asset_type_mandatory(
    data: { document_type_id: number; asset_type_id: number; mandatory: boolean }
  ): Observable<{ document_type_id: number; asset_type_id: number; mandatory: boolean }> {
    return this.makeRequest(
      "/documents/api/document-type-asset-type-mandatory/",
      "POST",
      data,
    );
  }

  public documents_document_type_asset_type_mandatory_bulk(
    data: { updates: { document_type_id: number; asset_type_id: number; mandatory: boolean }[] }
  ): Observable<{ document_type_id: number; asset_type_id: number; mandatory: boolean }[]> {
    return this.makeRequest(
      "/documents/api/document-type-asset-type-mandatory/bulk/",
      "POST",
      data,
    );
  }

  public documents_document_type_relationship_matrix(): Observable<KPI.DocumentTypeRelationshipMatrix> {
    return this.makeRequest<KPI.DocumentTypeRelationshipMatrix>("/documents/api/document-type-relationship-matrix/", "GET");
  }

  public documents_document_type_relationships(params?: any, waitAllPages: boolean = false): Observable<KPI.DocumentTypeRelationship[]> {
    return this.followPagination<KPI.DocumentTypeRelationship>("/documents/api/document-type-relationships/", params, waitAllPages);
  }

  public documents_link_to_entity(data: {
    document_id: number;
    app_label: string;
    model: string;
    object_id: number;
  }): Observable<any> {
    return this.makeRequest<any>(
      "/documents/api/link-document-to-entity/",
      "POST",
      data
    );
  }

  public documents_document_type_relationship_mandatory(
    data: { document_type_id: number; content_type_id: number; object_id: number; mandatory: boolean }
  ): Observable<{ document_type_id: number; content_type_id: number; object_id: number; mandatory: boolean }> {
    return this.makeRequest(
      "/documents/api/document-type-relationship-mandatory/",
      "POST",
      data,
    );
  }

  public documents_document_type_relationship_mandatory_bulk(
    data: { updates: { document_type_id: number; content_type_id: number; object_id: number; mandatory: boolean }[] }
  ): Observable<{ document_type_id: number; content_type_id: number; object_id: number; mandatory: boolean }[]> {
    return this.makeRequest(
      "/documents/api/document-type-relationship-mandatory/bulk/",
      "POST",
      data,
    );
  }

  public documents_document_type_relationship_action(
    data: { action: "create" | "update" | "delete"; document_type_relationship: number; document: Partial<KPI.Document> & { id?: number } }
  ): Observable<number | null> {
    return this.makeRequest<number | null>("/documents/api/document-type-relationship-action/", "POST", data);
  }

  public documents_update_linked_equipment(
    docId: number,
    linkedEquipmentIds: number[]
  ): Observable<{ linked_equipment: number[] }> {
    return this.makeRequest<{ linked_equipment: number[] }>(
      `/documents/api/documents/${docId}/linked-equipment/`,
      "PATCH",
      { linked_equipment: linkedEquipmentIds }
    );
  }

  public documents_update_linked_investment(
    docId: number,
    linkedInvestmentIds: number[]
  ): Observable<{ linked_investment: number[] }> {
    return this.makeRequest<{ linked_investment: number[] }>(
      `/documents/api/documents/${docId}/linked-investment/`,
      "PATCH",
      { linked_investment: linkedInvestmentIds }
    );
  }

  public documents_update_linked_initiative(
    docId: number,
    linkedInitiativeIds: number[]
  ): Observable<{ linked_initiative: number[] }> {
    return this.makeRequest<{ linked_initiative: number[] }>(
      `/documents/api/documents/${docId}/linked-initiative/`,
      "PATCH",
      { linked_initiative: linkedInitiativeIds }
    );
  }

  public import_asset_columns(): Observable<KPI.ColumnDefinition[]> {
    return this.makeRequest<KPI.ColumnDefinition[]>("/import/api/asset-columns/", "GET");
  }

  public import_asset_picture_columns(): Observable<KPI.ColumnDefinition[]> {
    return this.makeRequest<KPI.ColumnDefinition[]>("/import/api/asset-picture-columns/", "GET");
  }

  public import_import_asset(importedData): Observable<KPI.Asset> {
    return this.makeRequest<KPI.Asset>("/import/api/import-asset/", "POST", importedData);
  }

  public import_import_asset_picture(importedData): Observable<KPI.AssetPicture> {
    return this.makeRequest<KPI.AssetPicture>("/import/api/import-asset-picture/", "POST", importedData);
  }

  public import_import_investment(importedData): Observable<KPI.Investment> {
    return this.makeRequest<KPI.Investment>("/import/api/import-investment/", "POST", importedData);
  }

  public import_import_investment_picture(importedData): Observable<KPI.InvestmentPicture> {
    return this.makeRequest<KPI.InvestmentPicture>("/import/api/import-investment-picture/", "POST", importedData);
  }

  public import_opex_columns(): Observable<KPI.ColumnDefinition[]> {
    return this.makeRequest<KPI.ColumnDefinition[]>("/import/api/opex-columns/", "GET");
  }

  public import_import_opex(importedData): Observable<KPI.AssetOpex> {
    return this.makeRequest<KPI.AssetOpex>("/import/api/import-opex/", "POST", importedData);
  }

  public import_import_opex_bulk(importedData: any[]): Observable<any> {
    return this.makeRequest<any>("/import/api/import-opex/", "POST", importedData);
  }

  public import_importable_asset_statuses(): Observable<KPI.Display<KPI.ImportableAssetStatus>[]> {
    return this.makeRequest<KPI.Display<KPI.ImportableAssetStatus>[]>("/import/api/asset-statuses/", "GET");
  }

  public import_importable_investment_statuses(): Observable<KPI.Display<KPI.ImportableInvestmentStatus>[]> {
    return this.makeRequest<KPI.Display<KPI.ImportableInvestmentStatus>[]>("/import/api/investment-statuses/", "GET");
  }

  public import_investment_columns(): Observable<KPI.ColumnDefinition[]> {
    return this.makeRequest<KPI.ColumnDefinition[]>("/import/api/investment-columns/", "GET");
  }

  public import_investment_picture_columns(): Observable<KPI.ColumnDefinition[]> {
    return this.makeRequest<KPI.ColumnDefinition[]>("/import/api/investment-picture-columns/", "GET");
  }

  public import_mappings(): Observable<KPI.Mapping[]> {
    return this.makeRequest<KPI.Mapping[]>("/import/api/mappings/", "GET");
  }

  public import_mapping_patch(id: number, mapping: Partial<KPI.Mapping>): Observable<KPI.Mapping> {
    return this.makeRequest<KPI.Mapping>(`/import/api/mappings/${id}/`, "PATCH", mapping);
  }

  public import_mapping_post(mapping: Partial<KPI.Mapping>): Observable<KPI.Mapping> {
    return this.makeRequest<KPI.Mapping>("/import/api/mappings/", "POST", mapping);
  }

  public investments_investments(params?: any, waitAllPages: boolean = false, postParams=true): Observable<KPI.Investment[]> {
    return this.followPagination<KPI.Investment>(
      "/investments/api/investments/",
      params,
      waitAllPages,
      undefined,
      postParams
    );
  }

  public investments_attach_multiple_assets(investmentId: number, assetIds: number[]): Observable<{}> {
    return this.makeRequest<{}>(`/investments/api/investments/${investmentId}/assets/`, "PUT", { assets: assetIds });
  }
  public investments_detach_asset(investmentId: number, assetId: number): Observable<{}> {
    return this.makeRequest<{}>(`/investments/api/investments/${investmentId}/assets/${assetId}`, "DELETE");
  }

  public investments_available_values(options: any): Observable<{id: number, name: string}[]> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.makeRequest<{id: number, name: string}[]>(
      `/decision/api/equipment/available-values/`,
      'POST',
      options,
      null,
      headers,
    );
  }

  /**
   * deprecated, use investments_investments instead
   * @param params
   * @returns
   */
  public investments_investments_ex(params?: any, usePost=true): Observable<KPI.Investment[]> {
    // same thing than investments_investments but don't use pagination
    return this.apiGetList<KPI.Investment>("/investments/api/investments-pure-ex/", params, usePost);
  }

  public investments_investment_delete(id: number): Observable<void> {
    return this.makeRequest<void>(`/investments/api/investments/${id}/`, "DELETE");
  }

  public investments_investment_budget_origin(): Observable<KPI.InvestmentBudgetOrigin[]> {
    return this.makeRequest<KPI.InvestmentBudgetOrigin[]>("/investments/api/investment-budget-origins/", "GET");
  }

  public investments_investment_execution(): Observable<KPI.InvestmentExecution[]> {
    return this.makeRequest<KPI.InvestmentExecution[]>("/investments/api/investment-executions/", "GET");
  }


  public investments_investment_category_ordered(): Observable<KPI.InvestmentCategory[]> {
    return this.makeRequest<KPI.InvestmentCategory[]>("/investments/api/investment-categories-ordered/", "GET");
  }

  public investments_investment_category(): Observable<KPI.InvestmentCategory[]> {
    return this.makeRequest<KPI.InvestmentCategory[]>("/investments/api/investment-categories-ordered/", "GET");
  }

  public investments_investment_further_informations(
    params?: any,
    waitAllPages: boolean = false
  ): Observable<IdNameMixin[]> {
    return this.followPagination<IdNameMixin>(
      "/investments/api/investment-further-informations/",
      params,
      waitAllPages
    );
  }

  public investments_investment_scenarii(
    params?: any,
    waitAllPages: boolean = false
  ): Observable<KPI.InvestmentScenario[]> {
    return this.followPagination<KPI.InvestmentScenario>("/investments/api/investment-scenarii/", params, waitAllPages);
  }

  public get_investment_links(): Observable<KPI.InvestmentLink[]> {
    return this.makeRequest<KPI.InvestmentLink[]>("/investments/api/investment-links/", "GET");
  }

  public investments_investment_scenarii_post(
    investmentScenario: Partial<KPI.InvestmentScenario>
  ): Observable<KPI.InvestmentScenario> {
    return this.makeRequest<KPI.InvestmentScenario>(
      "/investments/api/investment-scenarii/",
      "POST",
      investmentScenario
    );
  }

  public investments_investment_scenarii_patch(
    id: number,
    investmentScenario: Partial<KPI.InvestmentScenario>
  ): Observable<KPI.InvestmentScenario> {
    return this.makeRequest<KPI.InvestmentScenario>(
      `/investments/api/investment-scenarii/${id}/`,
      "PATCH",
      investmentScenario
    );
  }

  public investments_investment_flag_scenarii(id: number): Observable<{}> {
    return this.makeRequest<{}>(`/investments/api/investment-scenarii/${id}/set-scenario-as-flagged/`, "PATCH");
  }

  public investments_investment_reset_scenarii(id: number): Observable<{}> {
    return this.makeRequest<{}>(`/investments/api/investment-scenarii/${id}/reset-scenario/`, "DELETE");
  }

  public investments_investment_scenarii_delete(id: number): Observable<void> {
    return this.makeRequest<void>(`/investments/api/investment-scenarii/${id}/`, "DELETE");
  }

  public investments_change_priority(investmentsIds: number[], newPriorityId: number): Observable<number[]> {
    const body = {
      investments: investmentsIds,
      new_investment_priority: newPriorityId
    };
    return this.makeRequest<number[]>(`/investments/api/change-investments-priority/`, "POST", body);
  }

  public investments_change_year(
    investmentIds: number[],
    currentYear: number,
    newYear: number,
    reason: number
  ): Observable<number[]> {
    const body = {
      investments: investmentIds,
      current_year: currentYear,
      new_year: newYear,
      postponment_reason: reason
    };
    return this.makeRequest<number[]>(`/investments/api/change-investment-year/`, "POST", body);
  }

  public investments_investment_total(params?: any): Observable<KPI.InvestmentTotal> {
    const headers = new HttpHeaders().set("Content-Type", "application/x-www-form-urlencoded");
    const param_helper = new URLSearchParams();
    for (const key in params) {
      if (key) {
        param_helper.set(key, params[key]);
      }
    }
    const body = param_helper.toString();

    return this.makeRequest<KPI.InvestmentTotal>("/investments/api/investment-total/", "POST", body, null, headers);
  }

  public investments_investment_total_budget_plan(params?: any): Observable<KPI.InvestmentTotalBudgetPlan> {
    const headers = new HttpHeaders().set("Content-Type", "application/x-www-form-urlencoded");
    const param_helper = new URLSearchParams();
    for (const key in params) {
      if (key) {
        param_helper.set(key, params[key]);
      }
    }
    const body = param_helper.toString();

    return this.makeRequest<KPI.InvestmentTotalBudgetPlan>(
      "/investments/api/investment-total-budget-plan/",
      "POST",
      body,
      null,
      headers
    );
  }

  public investments_investment_total_by_status(params?: any): Observable<KPI.InvestmentTotalByStatus> {
    const headers = new HttpHeaders().set("Content-Type", "application/x-www-form-urlencoded");
    const param_helper = new URLSearchParams();
    for (const key in params) {
      if (key) {
        param_helper.set(key, params[key]);
      }
    }
    const body = param_helper.toString();

    return this.makeRequest<KPI.InvestmentTotalByStatus>(
      "/investments/api/investment-total-by-status/",
      "POST",
      body,
      null,
      headers
    );
  }

  public investments_global_investments(params?: any): Observable<KPI.GlobalInvestments> {
    const headers = new HttpHeaders().set("Content-Type", "application/x-www-form-urlencoded");
    const param_helper = new URLSearchParams();
    for (const key in params) {
      if (key) {
        param_helper.set(key, params[key]);
      }
    }
    const body = param_helper.toString();

    return this.makeRequest<KPI.GlobalInvestments>("/investments/api/global-investments/", "POST", body, null, headers);
  }

  public investments_investment_notations(
    params?: any,
    waitAllPages: boolean = false
  ): Observable<KPI.InvestmentNotation[]> {
    return this.followPagination<KPI.InvestmentNotation>(
      "/investments/api/investment-notations/",
      params,
      waitAllPages
    );
  }

  // payload from `audit.NotationsSerializer`
  public investments_investment_create_notation(assetId: number, investmentId: number, auditNotation) {
    return this.makeRequest<KPI.InvestmentNotation>(
      `/investments/api/notation/${assetId}/${investmentId}/`,
      'POST',
      auditNotation,
    );
  }

  public investments_investment_notations_patch(
    id: number,
    investmentNotation: Partial<KPI.InvestmentNotation>
  ): Observable<KPI.InvestmentNotation> {
    return this.makeRequest<KPI.InvestmentNotation>(
      `/investments/api/investment-notations/${id}/`,
      "PATCH",
      investmentNotation
    );
  }

  public investments_investments_get(id: number): Observable<KPI.Investment> {
    return this.makeRequest<KPI.Investment>(`/investments/api/investments-pure/${id}/`, "GET");
  }

  public investments_by_ids_get(params: number[]): Observable<KPI.Investment[]> {
    const ids = params.join(",");
    return this.makeRequest<Pagination<KPI.Investment>>(`/investments/api/investments/?pk=${ids}`, "GET").pipe(
      map(response => response.results)
    );
  }

  public investments_investments_patch(id: number, investment: Partial<KPI.Investment>): Observable<KPI.Investment> {
    return this.makeRequest<KPI.Investment>(`/investments/api/investments/${id}/`, "PATCH", investment);
  }

  public investments_investment_statuses(): Observable<KPI.InvestmentStatus[]> {
    return this.makeRequest<KPI.InvestmentStatus[]>("/investments/api/investment-statuses/", "GET");
  }

  public investments_investment_statuses_patch(
    id: number,
    status: Partial<KPI.InvestmentStatus>
  ): Observable<KPI.InvestmentStatus> {
    return this.makeRequest<KPI.InvestmentStatus>(`/investments/api/investment-statuses/${id}/`, "PATCH", status);
  }

  public investments_investment_statuses_post(status: Partial<KPI.InvestmentStatus>): Observable<KPI.InvestmentStatus> {
    return this.makeRequest<KPI.InvestmentStatus>(`/investments/api/investment-statuses/`, "POST", status);
  }

  public investment_status_tansitions(): Observable<KPI.AllowedStatusTransition[]> {
    return this.followPagination<KPI.AllowedStatusTransition>("/investments/api/investment-status-transitions/");
  }

  public user_investments_status_transition(): Observable<KPI.AllowedStatusTransition[]> {
    return this.makeRequest("/investments/api/user-investment-status-transitions/", "GET");
  }

  public investments_status_change(investmentsIds: number[], current_status: number, new_status: number) {
    const body = {
      investments: investmentsIds,
      current_investment_status: current_status,
      new_investment_status: new_status
    };
    return this.makeRequest("/investments/api/change-investments-status/", "POST", body);
  }

  public investments_investment_priorities(): Observable<KPI.InvestmentPriority[]> {
    return this.makeRequest<KPI.InvestmentPriority[]>("/investments/api/investment-priorities/", "GET");
  }

  public investments_investment_history(id: number): Observable<KPI.History> {
    return this.makeRequest<KPI.History>(`/investments/api/investments/${id}/history/`, "GET");
  }

  public investments_create_investment(data: Object): Observable<KPI.Investment> {
    return this.makeRequest(`/investments/api/investments/`, "POST", data);
  }

  public investments_attach_investment(payload: {
    asset_id: number;
    asset_local_id?: string;
    investment_local_id?: string;
    investment_id?: number;
    detach?: boolean;
  }): Observable<KPI.Investment> {
    return this.makeRequest<KPI.Investment>(`/investments/api/attach-investment/`, "POST", JSON.stringify(payload));
  }

  public investments_attach_investment_and_asset(investmentId: number, assetId: number) {
    let data = {
      asset_id: assetId,
      investment_id: investmentId,
    };
    const url = "/investments/api/attach-investment/";
    return this.makeRequest<KPI.Investment>(url,"POST", data);
  }

  public investments_detach_investment_and_asset(investmentId: number, assetId: number) {
    let data = {
      asset_id: assetId,
      investment_id: investmentId,
      detach: true
    };
    const url = "/investments/api/attach-investment/";
    return this.makeRequest<KPI.Investment>(url,"POST", data);
  }

  /**
   * INVESTMENT TAGS
   */
  public investments_investment_reasons(
    params?: any,
    waitAllPages: boolean = false
  ): Observable<KPI.InvestmentReason[]> {
    return this.followPagination<KPI.InvestmentReason>("/investments/api/investment-reasons/", params, waitAllPages);
  }

  public investments_investment_reasons_patch(
    id: number,
    investmentReason: Partial<KPI.InvestmentReason>
  ): Observable<KPI.InvestmentReason> {
    return this.makeRequest<KPI.InvestmentReason>(
      `/investments/api/investment-reasons/${id}/`,
      "PATCH",
      investmentReason
    );
  }

  public investments_investment_reasons_post(
    investmentReason: Partial<KPI.InvestmentReason>
  ): Observable<KPI.InvestmentReason> {
    return this.makeRequest<KPI.InvestmentReason>("/investments/api/investment-reasons/", "POST", investmentReason);
  }

  public investments_investment_reasons_delete(investmentReasonId: number): Observable<KPI.InvestmentReason> {
    return this.makeRequest<KPI.InvestmentReason>(
      `/investments/api/investment-reasons/${investmentReasonId}/`,
      "DELETE"
    );
  }

  /*
   * INITIATIVE TAGS
   */

  public get_initiative_tags(): Observable<KPI.InitiativeTag[]> {
    return this.followPagination<KPI.InitiativeTag>(`/initiatives/api/initiative-tags/`);
  }

  public initiative_tags_patch(id: number, initiativeTag: Partial<KPI.InitiativeTag>): Observable<KPI.InitiativeTag> {
    return this.makeRequest<KPI.InitiativeTag>(`/initiatives/api/initiative-tags/${id}/`, "PATCH", initiativeTag);
  }

  public initiative_tags_post(initiativeTag: Partial<KPI.InitiativeTag>): Observable<KPI.InitiativeTag> {
    return this.makeRequest<KPI.InitiativeTag>("/initiatives/api/initiative-tags/", "POST", initiativeTag);
  }

  public initiative_tags_delete(initiativeTagId: number): Observable<KPI.InitiativeTag> {
    return this.makeRequest<KPI.InitiativeTag>(`/initiatives/api/initiative-tags/${initiativeTagId}/`, "DELETE");
  }

  /*
   * GENERIC GAMMES
   */

  public generic_gammes_list(params?: any): Observable<KPI.GenericGamme[]> {
    return this.followPagination<KPI.GenericGamme>("/gammes/generic-gammes/", params, true);
  }

  public generic_gammes_patch(id: number, payload: Partial<KPI.GenericGamme>): Observable<KPI.GenericGamme> {
    return this.makeRequest<KPI.GenericGamme>(`/gammes/generic-gammes/${id}/`, "PATCH", payload);
  }

  public generic_gamme_activations_list(params?: any): Observable<KPI.GenericGammeActivation[]> {
    return this.followPagination<KPI.GenericGammeActivation>("/gammes/generic-gamme-activations/", params, true);
  }

  public generic_gamme_activations_post(payload: Partial<KPI.GenericGammeActivation>): Observable<KPI.GenericGammeActivation> {
    return this.makeRequest<KPI.GenericGammeActivation>("/gammes/generic-gamme-activations/", "POST", payload);
  }

  public generic_gamme_activations_patch(id: number, payload: Partial<KPI.GenericGammeActivation>): Observable<KPI.GenericGammeActivation> {
    return this.makeRequest<KPI.GenericGammeActivation>(`/gammes/generic-gamme-activations/${id}/`, "PATCH", payload);
  }

  public gamme_sites_list(params?: any): Observable<KPI.GammeSite[]> {
    return this.followPagination<KPI.GammeSite>("/gammes/gamme-sites/", params, true);
  }

  public gamme_sites_post(payload: Partial<KPI.GammeSite>): Observable<KPI.GammeSite> {
    return this.makeRequest<KPI.GammeSite>("/gammes/gamme-sites/", "POST", payload);
  }

  public gamme_sites_patch(id: number, payload: Partial<KPI.GammeSite>): Observable<KPI.GammeSite> {
    return this.makeRequest<KPI.GammeSite>(`/gammes/gamme-sites/${id}/`, "PATCH", payload);
  }

  public gamme_sites_auto_init(perimeterId: number): Observable<KPI.GammeSite[]> {
    return this.makeRequest<KPI.GammeSite[]>("/gammes/gamme-sites/auto-init/", "POST", { perimeter: perimeterId });
  }

  public investments_investment_types(params?: any, waitAllPages: boolean = false): Observable<KPI.InvestmentType[]> {
    return this.followPagination<KPI.InvestmentType>("/investments/api/investment-types/", params, waitAllPages);
  }

  public investments_investment_types_patch(
    id: number,
    investmentType: Partial<KPI.InvestmentType>
  ): Observable<KPI.InvestmentType> {
    return this.makeRequest<KPI.InvestmentType>(`/investments/api/investment-types/${id}/`, "PATCH", investmentType);
  }

  public investments_investments_pictures(
    params?: any,
    waitAllPages: boolean = false
  ): Observable<KPI.InvestmentPicture[]> {
    return this.followPagination<KPI.InvestmentPicture>(`/investments/api/investment-pictures/`, params, waitAllPages);
  }

  public investments_postponements(params?: any, waitAllPages: boolean = false): Observable<KPI.Postponement[]> {
    return this.followPagination<KPI.Postponement>(`/investments/api/postponements/`, params, waitAllPages);
  }

  public investments_postponements_post(postponement: Partial<KPI.Postponement>): Observable<KPI.Postponement> {
    return this.makeRequest<KPI.Postponement>(`/investments/api/postponements/`, "POST", postponement);
  }

  public investments_postponement_reasons(
    params?: any,
    waitAllPages: boolean = false
  ): Observable<KPI.PostponementReason[]> {
    return this.followPagination<KPI.PostponementReason>(
      `/investments/api/postponement-reasons/`,
      params,
      waitAllPages
    );
  }

  public investments_invoices_recipient(): Observable<KPI.InvestmentInvoiceRecipient[]> {
    return this.makeRequest<KPI.InvestmentInvoiceRecipient[]>(`/investments/api/investment-invoices-recipient/`, "GET");
  }

  public investments_sponsor(): Observable<KPI.Sponsor[]> {
    return this.makeRequest<KPI.Sponsor[]>(`/investments/api/investment-sponsors/`, "GET");
  }

  public reference_data_reference_fields(
    params?: any,
    waitAllPages: boolean = false
  ): Observable<KPI.ReferenceField[]> {
    return this.followPagination<KPI.ReferenceField>("/reference-data/api/reference-fields/", params, waitAllPages);
  }

  public reference_data_reference_fields_patch(
    id: number,
    referenceField: Partial<KPI.ReferenceField>
  ): Observable<KPI.ReferenceField> {
    return this.makeRequest<KPI.ReferenceField>(`/reference-data/api/reference-fields/${id}/`, "PATCH", referenceField);
  }

  public reference_data_reference_fields_post(
    referenceField: Partial<KPI.ReferenceField>
  ): Observable<KPI.ReferenceField> {
    return this.makeRequest<KPI.ReferenceField>("/reference-data/api/reference-fields/", "POST", referenceField);
  }

  public reference_data_perimeter_reference_values(
    params?: any,
    waitAllPages: boolean = false
  ): Observable<KPI.PerimeterReferenceValue[]> {
    return this.followPagination<KPI.PerimeterReferenceValue>(
      "/reference-data/api/perimeter-reference-values/",
      params,
      waitAllPages
    );
  }

  public reference_data_perimeter_reference_values_patch(
    id: number,
    perimeterReferenceValue: Partial<KPI.PerimeterReferenceValue>
  ): Observable<KPI.PerimeterReferenceValue> {
    return this.makeRequest<KPI.PerimeterReferenceValue>(
      `/reference-data/api/perimeter-reference-values/${id}/`,
      "PATCH",
      perimeterReferenceValue
    );
  }

  public reference_field_patch(id: number, field: Partial<KPI.ReferenceField>): Observable<KPI.ReferenceField> {
    return this.makeRequest<KPI.ReferenceField>(`/reference-data/api/reference-fields/${id}/`, "PATCH", field);
  }

  public reference_field_post(field: Partial<KPI.ReferenceField>): Observable<KPI.ReferenceField> {
    return this.makeRequest<KPI.ReferenceField>(`/reference-data/api/reference-fields/`, "POST", field);
  }

  public reference_data_perimeter_reference_values_post(
    perimeterReferenceValue: Partial<KPI.PerimeterReferenceValue>
  ): Observable<KPI.PerimeterReferenceValue> {
    return this.makeRequest<KPI.PerimeterReferenceValue>(
      `/reference-data/api/perimeter-reference-values/`,
      "POST",
      perimeterReferenceValue
    );
  }

  public roadmap_audit_question_confidence_indices(
    params?: any,
    waitAllPages: boolean = false
  ): Observable<KPI.AuditQuestionConfidenceIndex[]> {
    return this.followPagination<KPI.AuditQuestionConfidenceIndex>(
      "/roadmap/api/audit-question-confidence-indices/",
      params,
      waitAllPages
    );
  }

  public roadmap_audit_question_confidence_indices_confidence_index(
    id: number,
    params?: any
  ): Observable<KPI.AuditQuestionConfidenceIndex> {
    return this.makeRequest<KPI.AuditQuestionConfidenceIndex>(
      `/roadmap/api/audit-question-confidence-indices/${id}/confidence-index/`,
      "GET",
      null,
      params
    );
  }

  public roadmap_chapters(params?: any, waitAllPages: boolean = false): Observable<KPI.Chapter[]> {
    return this.followPagination<KPI.Chapter>("/roadmap/api/chapters/", params, waitAllPages);
  }

  public roadmap_items(params?: any, waitAllPages: boolean = false): Observable<KPI.Item[]> {
    return this.followPagination<KPI.Item>("/roadmap/api/items/", params, waitAllPages);
  }

  public roadmap_control_points(params?: any, waitAllPages: boolean = true): Observable<KPI.ControlPoint[]> {
    return this.followPagination<KPI.ControlPoint>(`/roadmap/api/control-points/`, params, waitAllPages);
  }

  public roadmap_control_points_stats(): Observable<KPI.ControlPointsStats[]> {
    return this.makeRequest<KPI.ControlPointsStats[]>("/roadmap/api/control-points-stats/", "GET");
  }

  public roadmap_control_points_stats_by_year(): Observable<KPI.ControlPointsStatsByYear[]> {
    return this.makeRequest<KPI.ControlPointsStatsByYear[]>("/roadmap/api/control-points-stats-by-year/", "GET");
  }

  public roadmap_roadmap_indicators(params?: any): Observable<KPI.RoadmapIndicator[]> {
    return this.makeRequest<Pagination<KPI.RoadmapIndicator>>(
      "/roadmap/api/roadmap-indicators/",
      "GET",
      null,
      params
    ).pipe(map(({ results }) => results));
  }

  public roadmap_notations_roadmap_indicators(
    params: RoadmapNotationParams,
    waitAllPages: boolean = false
  ): Observable<KPI.RoadmapIndicatorNotation[]> {
    return this.followPagination<KPI.RoadmapIndicatorNotation>(
      "/roadmap/api/notations/roadmap-indicators/",
      params,
      waitAllPages,
      undefined,
      true
    );
  }

  public roadmap_notations_chapters(
    roadmapIndicator: number,
    params: RoadmapNotationParams,
    waitAllPages: boolean = false
  ): Observable<KPI.ChapterNotation[]> {
    return this.followPagination<KPI.ChapterNotation>(
      `/roadmap/api/notations/roadmap-indicators/${roadmapIndicator}/chapters/`,
      params,
      waitAllPages
    );
  }

  public roadmap_inventaire_by_perimeter_type(perimterTypeId: number, params: any): Observable<RoadmapInventaire> {
    return this.makeRequest(`/roadmap/api/inventaire/perimeter-type/${perimterTypeId}/`, "GET", null, params);
  }

  public roadmap_inventaire_toggle_chapter(payload: {
    perimeter_type_sub_level: number;
    perimeter_type_building: number;
    chapter_id: number;
    active: boolean;
  }) {
    return this.makeRequest<void>(`/roadmap/api/inventaire/${payload.chapter_id}/`, "PUT", payload);
  }

  public roadmap_notations_control_points(
    roadmapIndicator: number,
    chapter: number,
    params: RoadmapNotationParams
  ): Observable<KPI.ControlPointNotation[]> {
    return this.makeRequest<KPI.ControlPointNotation[]>(
      `/roadmap/api/notations/roadmap-indicators/${roadmapIndicator}/chapters/${chapter}/control-points/`,
      "GET",
      null,
      params
    );
  }

  // Get KPI Expert about defaults by KPI Expert Section
  public audit_kpi_expert_defaults(perimeter: number): Observable<KPI.KPIExpertSection[]> {
    return this.makeRequest<KPI.KPIExpertSection[]>(`/audit-expert/api/expert-statistic/${perimeter}`, "GET");
  }

  // A perimeter is a site when his buildings.length is > 1
  public audit_notations_sites(
    superCategory: number,
    category: number,
    subCategory: number,
    params: NotationParams
  ): Observable<KPI.PerimeterComputedNotation[]> {
    return this.followPagination<KPI.PerimeterComputedNotation>(
      `/audit/api/notations/super-categories/${superCategory}/categories/${category}/sub-categories/${subCategory}/sites/`,
      params,
      true,
      undefined,
      true
    );
  }

  public audit_notations_perimeter(
    superCategory: number,
    category: number,
    subCategory: number,
    site: number,
    params: NotationParams
  ): Observable<KPI.PerimeterComputedNotation[]> {
    return this.followPagination<KPI.PerimeterComputedNotation>(
      `/audit/api/notations/super-categories/${superCategory}/categories/${category}/sub-categories/${subCategory}/sites/${site}/perimeters/`,
      params,
      true,
      undefined,
      true
    );
  }

  /**
   ** INVESTMENTS CAPEX APIs
   **/

  // public audit_capex_global(params: Partial<NotationParams>): Observable<DeepPartial<KPI.GlobalComputedNotation>> {
  //   const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
  //   const param_helper = new URLSearchParams();
  //   for (const key in params) {
  //     if (key) {
  //       param_helper.set(key, params[key]);
  //     }
  //   }
  //   const body = param_helper.toString();

  //   return this.makeRequest<KPI.GlobalComputedNotation>('/investments/api/capex/', 'POST', body, null, headers);
  // }

  public audit_capex_super_categories(
    params: Partial<NotationParams>
  ): Observable<DeepPartial<KPI.SuperCategoryComputedNotation[]>> {
    const headers = new HttpHeaders().set("Content-Type", "application/x-www-form-urlencoded");
    const param_helper = new URLSearchParams();
    for (const key in params) {
      if (key) {
        param_helper.set(key, params[key]);
      }
    }
    const body = param_helper.toString();

    return this.makeRequest<KPI.SuperCategoryComputedNotation[]>(
      `/investments/api/capex/super-categories/`,
      "POST",
      body,
      null,
      headers
    );
  }

  public audit_capex_categories(
    superCategory: number,
    params: Partial<NotationParams>
  ): Observable<DeepPartial<KPI.CategoryComputedNotation[]>> {
    const headers = new HttpHeaders().set("Content-Type", "application/x-www-form-urlencoded");
    const param_helper = new URLSearchParams();
    for (const key in params) {
      if (key) {
        param_helper.set(key, params[key]);
      }
    }
    const body = param_helper.toString();

    return this.makeRequest<KPI.CategoryComputedNotation[]>(
      `/investments/api/capex/super-categories/${superCategory}/categories/`,
      "POST",
      body,
      null,
      headers
    );
  }

  public audit_capex_subcategories(
    superCategory: number,
    category: number,
    params: Partial<NotationParams>
  ): Observable<DeepPartial<KPI.SubCategoryComputedNotation[]>> {
    const headers = new HttpHeaders().set("Content-Type", "application/x-www-form-urlencoded");
    const param_helper = new URLSearchParams();
    for (const key in params) {
      if (key) {
        param_helper.set(key, params[key]);
      }
    }
    const body = param_helper.toString();

    return this.makeRequest<KPI.SubCategoryComputedNotation[]>(
      `/investments/api/capex/super-categories/${superCategory}/categories/${category}/sub-categories/`,
      "POST",
      body,
      null,
      headers
    );
  }

  public audit_capex_asset_types(
    superCategory: number,
    category: number,
    subCategory: number,
    params: Partial<NotationParams>,
    waitAllPages: boolean = false
  ): Observable<DeepPartial<KPI.AssetTypeComputedNotation[]>> {
    return this.followPagination<KPI.AssetTypeComputedNotation>(
      `/investments/api/capex/super-categories/${superCategory}/categories/${category}/sub-categories/${subCategory}/asset-types/`,
      params,
      waitAllPages,
      undefined,
      true
    );
  }

  public audit_capex_assets(
    superCategory: number,
    category: number,
    subCategory: number,
    assetType: number,
    params: Partial<NotationParams>,
    waitAllPages: boolean = true
  ): Observable<DeepPartial<KPI.AssetComputedNotation[]>> {
    return this.followPagination<KPI.AssetComputedNotation>(
      `/investments/api/capex/super-categories/${superCategory}/categories/${category}/sub-categories/${subCategory}/asset-types/${assetType}/assets/`,
      params,
      waitAllPages,
      undefined,
      true
    );
  }

  // A perimeter is a site when his buildings.length is > 1
  public audit_capex_sites(
    superCategory: number,
    category: number,
    subCategory: number,
    params: Partial<NotationParams>
  ): Observable<DeepPartial<KPI.PerimeterComputedNotation>[]> {
    return this.followPagination<KPI.PerimeterComputedNotation>(
      `/investments/api/capex/super-categories/${superCategory}/categories/${category}/sub-categories/${subCategory}/sites/`,
      params,
      true,
      undefined,
      true
    );
  }

  public audit_capex_perimeters(
    superCategory: number,
    category: number,
    subCategory: number,
    site: number,
    params: Partial<NotationParams>
  ): Observable<DeepPartial<KPI.PerimeterComputedNotation>[]> {
    return this.followPagination<KPI.PerimeterComputedNotation>(
      `/investments/api/capex/super-categories/${superCategory}/categories/${category}/sub-categories/${subCategory}/sites/${site}/perimeters/`,
      params,
      true,
      undefined,
      true
    );
  }

  public structures_clusters(params?: any, waitAllPages: boolean = false): Observable<KPI.Cluster[]> {
    return this.followPagination<KPI.Cluster>("/structures/api/clusters/", params, waitAllPages);
  }

  public structures_perimeter_types(params?: any, waitAllPages: boolean = false): Observable<KPI.PerimeterType[]> {
    return this.followPagination<KPI.PerimeterType>("/structures/api/perimeter-types/", params, waitAllPages);
  }

  public structures_perimeter(perimeterId: number, params?: any): Observable<KPI.Perimeter> {
    return this.makeRequest<KPI.Perimeter>(`/structures/api/perimeters/${perimeterId}`, "GET", null, params);
  }

  public add_multi_perimeter(options: FormData): Observable<KPI.Perimeter> {
    return this.makeRequest<KPI.Perimeter>(`/structures/api/perimeters/`, "POST", options, null, new HttpHeaders());
  }

  public update_multi_perimeter(perimeterId: number, options: any): Observable<KPI.Perimeter> {
    return this.makeRequest<KPI.Perimeter>(
      `/structures/api/perimeters/${perimeterId}/`,
      "PATCH",
      options,
      null,
      new HttpHeaders()
    );
  }

  public updateMultiPerimeter(multiPerimeter, data: Partial<KPI.Perimeter>): Observable<KPI.Perimeter> {
    const url = "/structures/api/update-multi-perimeter/" + multiPerimeter.id + "/";
    return this.makeRequest<KPI.Perimeter>(url, "PATCH", data);
  }

  public add_mono_perimeter(siteId: number, options: any): Observable<KPI.Perimeter> {
    return this.makeRequest<KPI.Perimeter>(
      "/structures/api/create-sub-perimeter/" + siteId + "/",
      "POST",
      options,
      null
    );
  }

  public update_mono_perimeter(perimeterId: number, options: any): Observable<KPI.Perimeter> {
    return this.makeRequest<KPI.Perimeter>(
      "/structures/api/update-mono-perimeter/" + perimeterId + "/",
      "PATCH",
      options,
      null
    );
  }

  public delete_mono_perimeter(perimeterId: number): Observable<void> {
    return this.makeRequest<void>("/structures/api/perimeters/" + perimeterId + "/", "DELETE");
  }

  public structures_perimeter_pictures(perimeterId: number): Observable<KPI.PerimeterPicture[]> {
    return this.followPagination<KPI.PerimeterPicture>("/structures/api/perimeter-pictures/", { perimeters: perimeterId });
  }

  public structures_create_perimeter_picture(formData: FormData): Observable<KPI.PerimeterPicture> {
    return this.makeRequest<KPI.PerimeterPicture>("/structures/api/perimeter-pictures/", "POST", formData, null, new HttpHeaders());
  }

  public structures_delete_perimeter_picture(id: number): Observable<void> {
    return this.makeRequest<void>(`/structures/api/perimeter-pictures/${id}/`, "DELETE");
  }

  public structures_update_perimeter_picture(id: number, data: Partial<KPI.PerimeterPicture>): Observable<KPI.PerimeterPicture> {
    return this.makeRequest<KPI.PerimeterPicture>(`/structures/api/perimeter-pictures/${id}/`, "PATCH", data);
  }

  public structures_set_perimeter_cover_image(perimeterId: number, pictureId: number | null): Observable<KPI.Perimeter> {
    return this.makeRequest<KPI.Perimeter>(
      `/structures/api/update-perimeter-cover-image/${perimeterId}/`,
      "PATCH",
      { cover_image: pictureId }
    );
  }

  public structures_countries(params?: any, waitAllPages: boolean = false): Observable<KPI.Country[]> {
    return this.followPagination<KPI.Country>("/structures/api/countries/", params, waitAllPages);
  }

  public structures_countries_emission_factors(sitesId: string): Observable<KPI.EmissionFactorByCountry[]> {
    return this.makeRequest<KPI.EmissionFactorByCountry[]>(
      `/structures/api/countries/emission-factors/?sites=${sitesId}`,
      "GET"
    );
  }

  public emission_factors_list(energyTypeId: number): Observable<KPI.EmissionFactorProvider[]> {
    return this.makeRequest<KPI.EmissionFactorProvider[]>(
      `/emission-factors/api/providers/energy-type/${energyTypeId}/`,
      "GET"
    );
  }

  public site_emission_factor_provider_patch(
    energyTypeId: number,
    payload: KPI.EmissionProviderPayload
  ): Observable<KPI.EmissionProviderPayload> {
    return this.makeRequest<KPI.EmissionProviderPayload>(
      `/emission-factors/api/site-emission-factors/energy-type/${energyTypeId}/`,
      "PATCH",
      payload
    );
  }

  public emission_factors_provider_details(providerId: number): Observable<KPI.EmissionFactorProvider> {
    return this.makeRequest<KPI.EmissionFactorProvider>(`/emission-factors/api/providers/${providerId}/detail/`, "GET");
  }

  public emission_factors_provider_delete(providerId: number): Observable<any> {
    return this.makeRequest<any>(`/emission-factors/api/providers/${providerId}/`, "DELETE");
  }

  public emission_factors_providers(): Observable<KPI.EmissionFactorProvider[]> {
    return this.makeRequest<KPI.EmissionFactorProvider[]>(`/emission-factors/api/providers/`, "GET");
  }

  public patch_provider_emission(
    providerId: number,
    options: KPI.PatchProviderEmissionFactorsOptions
  ): Observable<any> {
    return this.makeRequest<any>(`/emission-factors/api/providers/${providerId}/`, "PATCH", options);
  }

  public emission_factors_providers_by_site(energyTypeId: number): Observable<KPI.EmissionFactorBySiteResponse> {
    return this.makeRequest<any>(`/emission-factors/api/site-emission-factors/energy-type/${energyTypeId}`, "GET");
  }

  public tasks_tasks(params?: any, waitAllPages: boolean = false): Observable<KPI.Task[]> {
    return this.followPagination<KPI.Task>("/tasks/api/tasks/", params, waitAllPages);
  }

  public tasks_tasks_delete(id: number): Observable<void> {
    return this.makeRequest<void>(`/tasks/api/tasks/${id}/`, "DELETE");
  }

  public tasks_tasks_patch(id: number, task: Partial<KPI.Task>): Observable<KPI.Task> {
    return this.makeRequest<KPI.Task>(`/tasks/api/tasks/${id}/`, "PATCH", task);
  }

  public tasks_tasks_post(task: Partial<KPI.Task>): Observable<KPI.Task> {
    return this.makeRequest<KPI.Task>("/tasks/api/tasks/", "POST", task);
  }

  public tasks_tasks_bulk_post(groupTask: Partial<KPI.GroupTasks>): Observable<void> {
    return this.makeRequest("/tasks/api/tasks/bulk/", "POST", groupTask);
  }

  public tasks_task_states(): Observable<KPI.Display<KPI.TaskState>[]> {
    return this.makeRequest<KPI.Display<KPI.TaskState>[]>("/tasks/api/tasks/states/", "GET");
  }

  public users_google_login(body: any): Observable<any> {
    return this.makeRequest("/users/api/google-login/", "POST", body);
  }

  public users_microsoft_login(body: { accessToken: string; email: string }): Observable<any> {
    return this.makeRequest("/users/api/microsoft-login/signin/", "POST", body);
  }

  public get_corporates(): Observable<KPI.Corporate[]> {
    return this.followPagination<KPI.User>("/structures/api/corporates/").pipe(shareReplay(1));
  }

  public users_members(params?: any, waitAllPages: boolean = false): Observable<KPI.User[]> {
    return this.followPagination<KPI.User>("/users/api/members/", params, waitAllPages);
  }

  public users_mentions() {
    return this.makeRequest<KPI.User[]>('/users/api/members/mentions/', 'GET');
  }

  public users_members_post(user: KPI.UserInput): Observable<KPI.User> {
    return this.makeRequest<KPI.User>("/users/api/manage-user/", "POST", user);
  }

  public users_members_patch(id: number, user: Partial<KPI.UserInput>): Observable<KPI.User> {
    return this.makeRequest<KPI.User>(`/users/api/manage-user/${id}/`, "PATCH", user);
  }

  public users_members_delete(id: UserID): Observable<void> {
    return this.makeRequest<void>(`/users/api/manage-user/${id}/`, "DELETE");
  }

  public user_reset_password(id: number): Observable<any> {
    return this.makeRequest<any>(`/users/api/reset-password/`, "POST", { userId: id });
  }

  public users_members_me(): Observable<KPI.User> {
    return this.makeRequest<KPI.User>("/users/api/members/me/", "GET");
  }

  public group_permissions(): Observable<KPI.GroupPermission[]> {
    return this.makeRequest<KPI.GroupPermission[]>("/users/api/group-permissions/", "GET").pipe(shareReplay(1));
  }

  public users_members_groups(params?: any, waitAllPages: boolean = false): Observable<KPI.MembersGroup[]> {
    return this.followPagination<KPI.MembersGroup>("/users/api/members-groups/", params, waitAllPages);
  }

  public patch_members_group(id: number, params: Partial<KPI.MembersGroupInput>): Observable<KPI.MembersGroup> {
    return this.makeRequest<KPI.MembersGroup>(`/users/api/members-groups/${id}/`, "PATCH", params);
  }

  public post_members_group(params: Partial<KPI.MembersGroup>): Observable<KPI.MembersGroup> {
    return this.makeRequest<KPI.MembersGroup>(`/users/api/members-groups/`, "POST", params);
  }

  public delete_members_group(id: number): Observable<void> {
    return this.makeRequest<void>(`/users/api/members-groups/${id}/`, "DELETE");
  }

  public users_perimeter_permissions(params?: any): Observable<KPI.PerimeterPermission[]> {
    return this.makeRequest<KPI.PerimeterPermission[]>(`/users/api/perimeter-permissions/`, "GET", null, params);
  }

  public post_perimeter_permissions(params?: Partial<KPI.PerimeterPermission>): Observable<KPI.PerimeterPermission> {
    return this.makeRequest<KPI.PerimeterPermission>(`/users/api/perimeter-permissions/`, "POST", params);
  }

  public patch_perimeter_permissions(
    id: number,
    params?: Partial<KPI.PerimeterPermission>
  ): Observable<KPI.PerimeterPermission> {
    return this.makeRequest<KPI.PerimeterPermission>(`/users/api/perimeter-permissions/${id}/`, "PATCH", params);
  }

  public delete_perimeter_permissions(id: number): Observable<void> {
    return this.makeRequest<void>(`/users/api/perimeter-permissions/${id}/`, "DELETE");
  }

  public get_initiative(id: number): Observable<KPI.Initiative> {
    return this.makeRequest<KPI.Initiative>(`/initiatives/api/initiatives/${id}/`, "GET");
  }

  public get_initiatives(options: { perimeters?: string }): Observable<KPI.Initiative[]> {
    return this.followPagination<KPI.Initiative>(`/initiatives/api/initiatives/`, options);
  }

  public initiatives_initiatives_post(initiative: Partial<KPI.Initiative>): Observable<KPI.Initiative> {
    return this.makeRequest<KPI.Initiative>("/initiatives/api/initiatives/", "POST", initiative);
  }

  public initiatives_initiatives_patch(id: number, initiative: Partial<KPI.Initiative>): Observable<KPI.Initiative> {
    return this.makeRequest<KPI.Initiative>(`/initiatives/api/initiatives/${id}/`, "PATCH", initiative);
  }

  public initiatives_initiatives_delete(id: number): Observable<void> {
    return this.makeRequest<void>(`/initiatives/api/initiatives/${id}/`, "DELETE");
  }

  public initiatives_status_change(options: any) {
    return this.makeRequest("/initiatives/api/change-initiatives-status/", "POST", options);
  }

  public get_initiative_statuses(): Observable<KPI.InitiativeStatus[]> {
    return this.followPagination<KPI.InitiativeStatus>(`/initiatives/api/initiative-statuses/`);
  }

  public get_initiative_types(): Observable<KPI.InitiativeType[]> {
    return this.followPagination<KPI.InitiativeType>(`/initiatives/api/initiative-types/`);
  }

  public get_initiative_owners(): Observable<KPI.InitiativeOwner[]> {
    return this.followPagination<KPI.InitiativeOwner>(`/initiatives/api/initiative-owners/`);
  }

  public get_initiative_energy_types(): Observable<KPI.InitiativeEnergyType[]> {
    return this.followPagination<KPI.InitiativeEnergyType>(`/initiatives/api/energy-types/`);
  }

  public get_emission_categories(): Observable<KPI.EmissionCategory[]> {
    return this.followPagination<KPI.EmissionCategory>(`/initiatives/api/emission-categories/`);
  }

  public get_gain_origins(): Observable<KPI.GainOrigin[]> {
    return this.followPagination<KPI.GainOrigin>(`/initiatives/api/gain-origins`);
  }

  public get_gain_precisions(): Observable<KPI.GainPrecision[]> {
    return this.followPagination<KPI.GainPrecision>(`/initiatives/api/gain-precisions`);
  }

  public get_initiative_cash_flow(initiativeId: number, queryParams): Observable<KPI.InitiativeCashFlow> {
    return this.makeRequest<KPI.InitiativeCashFlow>(
      `/decision/api/initiative-cashflow/${initiativeId}`,
      "GET",
      {},
      queryParams
    );
  }

  public get_energy_price_hypotheses(): Observable<KPI.InflationHypothesis[]> {
    return this.makeRequest<KPI.InflationHypothesis[]>(`/initiatives/api/energy-price-hypothesis/`, "GET");
  }

  public get_energy_report_sections(): Observable<KPI.EnergyReportSection[]> {
    return this.makeRequest<KPI.EnergyReportSection[]>(`/initiatives/api/energy-report-sections/`, "GET");
  }

  public get_project(id: number, queryParams): Observable<KPI.Project> {
    return this.makeRequest<KPI.Project>(`/projects/api/projects/${id}/`, "GET", {}, queryParams);
  }

  public project_patch(id: number, project: Partial<KPI.Project>): Observable<KPI.Project> {
    return this.makeRequest<KPI.Project>(`/projects/api/projects/${id}/`, "PATCH", project);
  }

  public project_post(project: Partial<KPI.Project>): Observable<KPI.Project> {
    return this.makeRequest<KPI.Project>("/projects/api/projects/", "POST", project);
  }

  public project_delete(id: number): Observable<void> {
    return this.makeRequest<void>(`/projects/api/projects/${id}/`, "DELETE");
  }

  public project_instances_patch(
    projectId: number,
    instances: Partial<KPI.WriteableProjectInstance[]>
  ): Observable<KPI.Project> {
    return this.makeRequest<KPI.Project>(`/projects/api/update-project/${projectId}/instances/`, "POST", { instances });
  }

  public project_post_global_gain(id: number, value: boolean): Observable<KPI.Project> {
    return this.makeRequest<KPI.Project>(`/projects/api/update-project/${id}/global-gain/`, "POST", {
      global_gain: value
    });
  }

  public project_post_gains(id: number, gains: KPI.ProjectGain[]): Observable<KPI.Project> {
    return this.makeRequest<KPI.Project>(`/projects/api/update-project/${id}/gains/`, "POST", { gains });
  }

  public project_post_initiatives_and_investments(
    id: number,
    options: {
      initiatives?: number[];
      investments?: number[];
    }
  ): Observable<KPI.Project> {
    return this.makeRequest<KPI.Project>(`/projects/api/update-project/${id}/initiatives/`, "POST", options);
  }

  public get_projects(queryParams: { buildings?: string, investment?: number }): Observable<KPI.Project[]> {
    return this.makeRequest<KPI.Project[]>(`/projects/api/projects/`,
      'GET',
      {},
      queryParams,
    )
  }

  public get_perimeter_stats(queryParams): Observable<KPI.PerimeterStats | KPI.PerimeterStatsDetails> {
    return this.makeRequest<KPI.PerimeterStats>(`/decision/api/perimeter-stats/`, "GET", {}, queryParams);
  }

  public get_perimeter_energy_stats(siteId: number, queryParams): Observable<KPI.PerimeterEnergyStats> {
    return this.makeRequest<KPI.PerimeterEnergyStats>(
      `/statistic/api/site-energy-trajectory/${siteId}/`,
      "GET",
      {},
      queryParams
    );
  }

  public get_perimeter_energy_ids(queryParams): Observable<number[]> {
    return this.makeRequest<any>(`/statistic/api/perimeter-energy-ids`, "GET", {}, queryParams);
  }

  public get_energy_objectives(queryParams): Observable<KPI.ObjectivesPathway> {
    return this.makeRequest<KPI.ObjectivesPathway>(`/initiatives/api/perimeter-objectives/`, "GET", {}, queryParams);
  }

  public get_energy_objectives_list(params: {
    buildings: string;
    start_year: number;
    end_year: number;
  }): Observable<any[]> {
    return this.makeRequest<any[]>(`/initiatives/api/energy-objectives/`, "GET", {}, params);
  }

  public get_energy_objective_detail(objectiveId: number): Observable<any> {
    return this.makeRequest<any>(`/initiatives/api/energy-objectives/${objectiveId}/`, "GET");
  }

  public create_energy_objective(data: {
    site_id: number;
    sub_perimeter_id: number;
    year: number;
    consumption: number;
  }): Observable<any> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<any>(`/initiatives/api/energy-objectives/`, "POST", data, null, headers);
  }

  public update_energy_objective(
    objectiveId: number,
    data: {
      site_id: number;
      sub_perimeter_id: number;
      year: number;
      consumption: number;
    }
  ): Observable<any> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<any>(`/initiatives/api/energy-objectives/${objectiveId}/`, "PUT", data, null, headers);
  }

  public delete_energy_objective(objectiveId: number): Observable<void> {
    return this.makeRequest<void>(`/initiatives/api/energy-objectives/${objectiveId}?type=energy`, "DELETE");
  }

  public get_carbon_objectives_list(params: {
    buildings: string;
    start_year: number;
    end_year: number;
  }): Observable<any[]> {
    return this.makeRequest<any[]>(`/initiatives/api/energy-objectives/`, "GET", null, params);
  }

  public get_carbon_objective_detail(objectiveId: number): Observable<any> {
    return this.makeRequest<any>(`/initiatives/api/energy-objectives/${objectiveId}/`, "GET");
  }

  public create_carbon_objective(data: {
    site_id: number;
    sub_perimeter_id: number;
    year: number;
    emission: number;
  }): Observable<any> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<any>(`/initiatives/api/energy-objectives/`, "POST", data, null, headers);
  }

  public update_carbon_objective(
    objectiveId: number,
    data: {
      site_id: number;
      sub_perimeter_id: number;
      year: number;
      emission: number;
    }
  ): Observable<any> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<any>(`/initiatives/api/energy-objectives/${objectiveId}/`, "PUT", data, null, headers);
  }

  public delete_carbon_objective(objectiveId: number): Observable<void> {
    return this.makeRequest<void>(`/initiatives/api/energy-objectives/${objectiveId}?type=carbon`, "DELETE");
  }

  public get_customer_config(): Observable<KPI.CustomerConfig[]> {
    return this.makeRequest<KPI.CustomerConfig[]>(`/config/api/customer-config/`, "GET");
  }

  public get_sites_map_legend_items(): Observable<KPI.SitesMapLegendItem[]> {
    return this.makeRequest<KPI.SitesMapLegendItem[]>(`/config/api/sites-map-legend-items/`, "GET");
  }

  public get_project_types(): Observable<KPI.ProjectType[]> {
    return this.followPagination<KPI.ProjectType>(`/projects/api/project-types/`);
  }

  public get_price_sheet(priceSheetId: number): Observable<KPI.PriceSheet> {
    return this.makeRequest<KPI.PriceSheet>(`/assets/api/price-sheet-detail/${priceSheetId}/`, "GET");
  }

  public get_price_sheets_by_asset_type(assetTypeId: number): Observable<KPI.PriceSheet[]> {
    return this.makeRequest<KPI.PriceSheet[]>(`/assets/api/price-sheets/${assetTypeId}/`, "GET");
  }

  public get_price_sheet_cost_items(): Observable<KPI.PriceSheetCostItem[]> {
    return this.makeRequest<KPI.PriceSheetCostItem[]>(`/assets/api/price-sheet-cost-items/`, "GET");
  }

  public create_price_sheet(assetTypeId: number, options: any): Observable<KPI.PriceSheet> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<KPI.PriceSheet>(
      `/assets/api/add-price-sheet/${assetTypeId}/`,
      "POST",
      options,
      null,
      headers
    );
  }

  public update_price_sheet(priceSheetId: number, options: any): Observable<KPI.PriceSheet> {
    return this.makeRequest<KPI.PriceSheet>(`/assets/api/update-price-sheet/${priceSheetId}/`, "PATCH", options);
  }

  public delete_price_sheet(priceSheetId: number): Observable<KPI.PriceSheet> {
    return this.makeRequest<KPI.PriceSheet>(`/assets/api/price-sheet-detail/${priceSheetId}/`, "DELETE");
  }

  public get_site_consumptions(multiPerimeterId): Observable<KPI.SiteConsumption> {
    return this.makeRequest<KPI.SiteConsumption>(`/initiatives/api/energy-consumption/${multiPerimeterId}`, "GET");
  }

  public add_year_consumption(options: KPI.YearConsumptionInput): Observable<any> {
    return this.makeRequest<any>(`/initiatives/api/year-consumption/`, "POST", options);
  }

  public update_year_consumption(trajectoryRecordId: number, options: KPI.YearConsumptionInput): Observable<any> {
    return this.makeRequest<any>(`/initiatives/api/year-consumption/${trajectoryRecordId}/`, "PATCH", options);
  }

  public delete_year_consumption(trajectoryRecordId: number): Observable<any> {
    return this.makeRequest<any>(`/initiatives/api/year-consumption/${trajectoryRecordId}/`, "DELETE");
  }

  public get_refrigerant_types(): Observable<KPI.RefrigerantType[]> {
    return this.followPagination<KPI.RefrigerantType>(`/assets/api/refrigerant-types/`);
  }

  public get_perimeter_energy_trajectories(options: { site_id: number }): Observable<KPI.PerimeterEnergyTrajectory[]> {
    return this.followPagination<KPI.PerimeterEnergyTrajectory>(
      `/initiatives/api/perimeter-energy-trajectories/`,
      options
    );
  }

  public delete_perimeter_energy_trajectory(id: number): Observable<any> {
    return this.makeRequest<any>(`/initiatives/api/perimeter-energy-trajectories/${id}/`, "DELETE");
  }

  public add_perimeter_energy_trajectory(
    options: RequireAtLeastOne<KPI.PerimeterEnergyTrajectory>
  ): Observable<KPI.PerimeterEnergyTrajectory> {
    return this.makeRequest<KPI.PerimeterEnergyTrajectory>(
      `/initiatives/api/perimeter-energy-trajectories/`,
      "POST",
      options
    );
  }

  public add_trajectory_records(options: RequireAtLeastOne<KPI.TrajectoryRecord>): Observable<KPI.TrajectoryRecord> {
    return this.makeRequest<KPI.TrajectoryRecord>(`/initiatives/api/trajectory-records/`, "POST", options);
  }

  public update_trajectory_records(
    recordId: number,
    options: RequireAtLeastOne<KPI.TrajectoryRecord>
  ): Observable<KPI.TrajectoryRecord> {
    return this.makeRequest<KPI.TrajectoryRecord>(`/initiatives/api/trajectory-records/${recordId}/`, "PATCH", options);
  }

  public get_initiative_confidence_on_feasibility(options): Observable<KPI.InitiativeFeasibilityData> {
    return this.makeRequest<KPI.InitiativeFeasibilityData>(
      `/initiatives/api/initiative-confidence-on-feasibility/`,
      "GET",
      {},
      options
    );
  }

  public get_lifetime_vs_theorical(options: any): Observable<KPI.LifetimeVsTheorical> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.makeRequest<KPI.LifetimeVsTheorical>(
      `/decision/api/lifetime-vs-theorical/`,
      "POST",
      options,
      null,
      headers
    );
  }

  // Use the following function to get the get the budget reporting for a single project !! Might need some refactoring !!

  // public get_budget_reporting(buildingIds: string): Observable<KPI.BudgetReporting[]> {
  //   return this.makeRequest<KPI.BudgetReporting[]>(
  //     `/statistic/api/budget-reporting?buildings=${buildingIds}`, 'GET');
  // }

  public get_budget_reporting_by_investment(investIds: number[]): Observable<KPI.BudgetReporting[]> {
    return this.makeRequest<KPI.BudgetReporting[]>(
      `/statistic/api/budget-reporting?investments=${investIds.join(",")}`,
      "GET",
      {}
    );
  }

  public get_global_budget_history(year: number, buildings: string): Observable<KPI.BudgetReporting[]> {
    return this.makeRequest<KPI.BudgetReporting[]>(
      `/statistic/api/budget-reporting?buildings=${buildings}&year=${year}`,
      "GET",
      {}
    );
  }

  public get_investments_monitoring(params: {buildings: string}): Observable<KPI.InvestmentMonitoring> {
    return this.makeRequest<KPI.InvestmentMonitoring>("/investments/api/investment-monitoring/", "POST", {buildings: params.buildings});
  }

  public get_environmental_profiles_by_asset_type(assetTypeId: number): Observable<KPI.EnvironmentalProfile[]> {
    return this.makeRequest<KPI.EnvironmentalProfile[]>(`/assets/api/environmental-profiles/${assetTypeId}/`, "GET");
  }

  public get_site_contracts(siteIds?: number[]): Observable<KPI.EnergyContract[]> {
    const sites = siteIds ? siteIds.join(",") : "";
    return this.makeRequest<KPI.EnergyContract[]>(`/energy/api/energy-contracts/?perimeterIds=${sites}`, "GET");
  }

  public get_site_counters(siteIds: number[]): Observable<KPI.EnergyCounter[]> {
    const sites = siteIds ? siteIds.join(",") : "";
    return this.makeRequest<KPI.EnergyCounter[]>(`/energy/api/counters/?perimeterIds=${sites}`, "GET");
  }

  public get_site_energy_data_monthly(body: any): Observable<KPI.EnergyData> {
    return this.makeRequest<KPI.EnergyData>(`/energy/api/perimeter-monthly-energy/`, "POST", body);
  }

  public get_site_energy_data_corrected_monthly(body: any): Observable<KPI.EnergyDataCorrected> {
    return this.makeRequest<KPI.EnergyDataCorrected>(
      `/energy/api/perimeter-monthly-energy-consumption-correction-with-energy-type/`,
      "POST",
      body
    );
  }

  public get_site_energy_emission_data_monthly(body: any): Observable<KPI.EnergyCarbonData> {
    return this.makeRequest<KPI.EnergyCarbonData>(`/energy/api/carbon-monthly-trajectory/`, "POST", body);
  }

  public create_energy_contract(contract: KPI.EnergyContract): Observable<KPI.EnergyContract> {
    return this.makeRequest<KPI.EnergyContract>(`/energy/api/energy-contracts/`, "POST", contract);
  }

  public update_energy_contract(contract: KPI.EnergyContract): Observable<KPI.EnergyContract> {
    return this.makeRequest<KPI.EnergyContract>(
      `/energy/api/energy-contracts/` + contract.id.toString() + `/`,
      "PUT",
      contract
    );
  }

  public create_energy_counter(counter: KPI.EnergyCounterCreation): Observable<KPI.EnergyCounter> {
    return this.makeRequest<KPI.EnergyCounter>(`/energy/api/counters/`, "POST", counter);
  }

  public get_measurement_units(): Observable<KPI.MeasurementUnit[]> {
    return this.followPagination<KPI.MeasurementUnit>(`/energy/api/measurement-units/`, "GET");
  }

  public get_asset_types_replacements(assetTypeIds: number[]): Observable<KPI.AssetTypeReplacementResponse> {
    const body = {
      asset_types: assetTypeIds
    };
    return this.makeRequest<KPI.AssetTypeReplacementResponse>(`/assets/api/asset-type-replacements/`, "POST", body);
  }

  public post_auto_investment_plan(
    preview: boolean,
    items: AutoPPAInput[],
    price_sheets: { asset: number; pricesheet: number; price_tier?: string }[],
    eol_type: KPI.TheoreticalOrAuditFilter
  ): Observable<KPI.AutoPPARow[]> {
    const body = { preview, items, price_sheets, eol_type };
    return this.makeRequest<KPI.AutoPPARow[]>(`/investments/api/auto-investment-plan/`, "POST", body);
  }

  public has_running_round(perimeterId: number, year: number): Observable<boolean> {
    return this.makeRequest<boolean>(`/workflow/api/has-running-round/${perimeterId}/${year}/`, "GET").pipe(
      map(res => res?.["running_round"])
    );
  }

  public get_workflow_proposed_total_from_tags(
    tags: number[],
    sites: number[],
    investmentPlan: number
  ): Observable<{ total_investment: number; total_price: number }> {
    return this.makeRequest(`/workflow/api/proposed-total-from-tags/`, "POST", { tags, sites, investmentPlan });
  }

  public get_remaining_flash_budget(perimeterId: number, year: number, investmentId: number | null): Observable<number> {
    const params = investmentId != null ? { ignore: investmentId } : {};
    return this.makeRequest<number>(
      `/workflow/api/check-flash-investments-budget/${perimeterId}/${year}/`,
      "GET",
      null,
      params
    );
  }

  public get_investment_plans(): Observable<KPI.InvestmentPlan[]> {
    return this.makeRequest<KPI.InvestmentPlan[]>("/workflow/api/investment-plans/", "GET");
  }

  public get_workflow_steps(): Observable<KPI.WorkflowStep[]> {
    return this.makeRequest<KPI.WorkflowStep[]>("/workflow/api/workflow-steps/", "GET");
  }

  public get_workflow_waiting_sites(
    plan: KPI.InvestmentPlan,
    perimeters: KPI.Perimeter[]
  ): Observable<KPI.WorkflowSite[]> {
    const perimeterIds = perimeters.map(elt => elt.id);
    if (plan?.id) {
      return this.makeRequest<KPI.WorkflowSite[]>("/workflow/api/waiting-for-round/" + plan.id + "/", "POST", {
        perimeters: perimeterIds
      });
    } else {
      this.errorService.handleError(_("No investment plan available"), true);
    }
  }

  public create_workflow_round(
    plan: KPI.InvestmentPlan,
    name: string,
    perimeters: KPI.Perimeter[],
    steps: KPI.WorkflowStep[],
    selectedTagIds: number[],
    projectCode?: string,
  ): Observable<KPI.WorkflowRoundCreation> {
    const perimeterIds = perimeters.map(elt => elt.id);
    return this.makeRequest<KPI.WorkflowRoundCreation>("/workflow/api/create-round/" + plan.id + "/", "POST", {
      perimeters: perimeterIds,
      name: name,
      steps: steps,
      tag_ids: selectedTagIds,
      from_app: this.getFromApp(),
      project_code: projectCode || null,
    });
  }

  public get_workflow_rounds(plan: KPI.InvestmentPlan, perimeters: KPI.Perimeter[]): Observable<KPI.WorkflowRound[]> {
    const perimeterIds = perimeters.map(elt => elt.id);
    return this.makeRequest<KPI.WorkflowRound[]>("/workflow/api/rounds/" + plan?.id + "/", "POST", {
      perimeters: perimeterIds
    });
  }

  public get_workflow_separate_rounds(
    plan: KPI.InvestmentPlan,
    perimeters: KPI.Perimeter[]
  ): Observable<KPI.WorkflowRoundsListResponse> {
    const perimeterIds = perimeters.map(elt => elt.id);
    return this.makeRequest<KPI.WorkflowRoundsListResponse>("/workflow/api/rounds-separate/" + plan?.id + "/", "POST", {
      perimeters: perimeterIds
    });
  }

  public get_workflow_history(
    plan: KPI.InvestmentPlan,
    perimeters: KPI.Perimeter[]
  ): Observable<KPI.WorkflowRoundHistory[]> {
    const perimeterIds = perimeters.map(elt => elt.id);
    return this.makeRequest<KPI.WorkflowRoundHistory[]>("/workflow/api/rounds-history/" + plan.id + "/", "POST", {
      perimeters: perimeterIds
    });
  }

  public get_workflow_round_sites(round: KPI.WorkflowRound): Observable<KPI.WorkflowRoundResponse> {
    return this.makeRequest<KPI.WorkflowRoundResponse>("/workflow/api/round-sites/" + round.id + "/", "GET");
  }

  public get_workflow_round_detail(round: KPI.WorkflowRoundHistory): Observable<KPI.WorkflowRoundSite[]> {
    return this.makeRequest<KPI.WorkflowRoundSite[]>("/workflow/api/round/" + round.id + "/", "GET");
  }

  public delete_workflow_round(round: KPI.WorkflowRoundHistory): Observable<KPI.WorkflowStatus> {
    return this.makeRequest<KPI.WorkflowStatus>("/workflow/api/round/" + round.id + "/", "delete");
  }

  public getFromApp(): string {
    return window.location.protocol + "//" + window.location.host;
  }

  public investment_notify(
    investmentId: number,
    emails: string[],
    message: string
  ): Observable<{ notified_emails: string[] }> {
    return this.makeRequest<{ notified_emails: string[] }>(
      `/workflow/api/investment-notify/${investmentId}/`,
      "POST",
      {
        from_app: this.getFromApp(),
        emails,
        message,
      }
    );
  }

  ack_workflow_steps(
    round: KPI.WorkflowRound,
    stepId: number,
    ack_sites: number[],
    stop_sites: number[]
  ): Observable<KPI.WorkflowStatusEx> {
    return this.makeRequest<KPI.WorkflowStatusEx>("/workflow/api/ack-steps/", "POST", {
      round: round.id,
      step: stepId,
      ack_perimeters: ack_sites,
      stop_perimeters: stop_sites,
      from_app: this.getFromApp()
    });
  }

  close_workflow_sites(round: KPI.WorkflowRound, sites: KPI.WorkflowSite[]) {
    return this.makeRequest<{ round_status: KPI.WorkflowClosureStatus; status: string }>(
      "/workflow/api/close-sites/",
      "POST",
      { round: round.id, perimeters: sites.map(elt => elt.id) }
    );
  }

  public get_workflow_flash_budget(
    plan: KPI.InvestmentPlan,
    perimeters: KPI.Perimeter[]
  ): Observable<KPI.WorkflowFlashBudget> {
    const perimeterIds = perimeters.map(elt => elt.id);
    return this.makeRequest<KPI.WorkflowFlashBudget>("/workflow/api/flash-budget/" + plan.id + "/", "POST", {
      perimeters: perimeterIds
    });
  }

  public get_workflow_flash_investments(
    plan: KPI.InvestmentPlan,
    perimeters: KPI.Perimeter[]
  ): Observable<KPI.WorkflowFlashInvestments> {
    const perimeterIds = perimeters.map(elt => elt.id);
    return this.makeRequest<KPI.WorkflowFlashInvestments>("/workflow/api/flash-investments/" + plan.id + "/", "POST", {
      perimeters: perimeterIds
    });
  }

  public set_workflow_flash_site_budget(
    plan: KPI.InvestmentPlan,
    perimeters: KPI.Perimeter[],
    siteBudgets: KPI.WorkflowFlashSiteBudget[]
  ): Observable<KPI.WorkflowFlashBudget> {
    const perimeterIds: number[] = perimeters.map(elt => elt.id);
    const sites: any[] = siteBudgets.map(elt => {
      return {
        perimeter: elt.site.id,
        budget: elt.budget
      };
    });
    return this.makeRequest<KPI.WorkflowFlashBudget>("/workflow/api/flash-budget/" + plan.id + "/", "POST", {
      perimeters: perimeterIds,
      sites: sites
    });
  }

  public set_workflow_flash_global_budget(
    plan: KPI.InvestmentPlan,
    perimeters: KPI.Perimeter[],
    budget: number
  ): Observable<KPI.WorkflowFlashBudget> {
    const perimeterIds: number[] = perimeters.map(elt => elt.id);
    return this.makeRequest<KPI.WorkflowFlashBudget>("/workflow/api/flash-budget/" + plan.id + "/", "POST", {
      perimeters: perimeterIds,
      global_budget: budget
    });
  }

  public get_workflow_perimeter_plans(
    plan: KPI.InvestmentPlan,
    perimeters: KPI.Perimeter[]
  ): Observable<KPI.WorkflowPerimeterPlan[]> {
    const perimeterIds: number[] = perimeters.map(elt => elt.id);
    return this.makeRequest<KPI.WorkflowPerimeterPlan[]>("/workflow/api/perimeter-plans/" + plan.id + "/", "POST", {
      perimeters: perimeterIds
    });
  }

  public workflow_close_perimeter_plans(
    plan: KPI.InvestmentPlan,
    perimeters: KPI.Perimeter[]
  ): Observable<KPI.WorkflowStatus> {
    const perimeterIds: number[] = perimeters.map(elt => elt.id);
    return this.makeRequest<KPI.WorkflowStatus>("/workflow/api/close-perimeter-plans/" + plan.id + "/", "POST", {
      perimeters: perimeterIds
    });
  }

  public post_workflow_comment(
    round: number,
    site: number,
    step: number,
    text: string
  ): Observable<KPI.WorkflowRoundStepComment> {
    return this.makeRequest<KPI.WorkflowRoundStepComment>("/workflow/api/comment-step/", "POST", {
      round: round,
      perimeter: site,
      step: step,
      text: text,
      from_app: this.getFromApp()
    });
  }

  public patch_workflow_comment(
    comment: KPI.WorkflowRoundStepComment,
    text: string
  ): Observable<KPI.WorkflowRoundStepComment> {
    return this.makeRequest<KPI.WorkflowRoundStepComment>("/workflow/api/comment-step/" + comment.id + "/", "PATCH", {
      text: text
    });
  }

  public delete_workflow_comment(comment: KPI.WorkflowRoundStepComment): Observable<any> {
    return this.makeRequest<any>("/workflow/api/comment-step/" + comment.id + "/", "DELETE");
  }
  /**Quote part API */
  //get check user quote part permission
  public get_quote_part_permission(): Observable<QuotePartPermission> {
    return this.makeRequest<QuotePartPermission>("/quote-parts/api/user-quote-part-permission/", "GET");
  }

  public get_quote_parts() {
    return this.makeRequest<QuotePartItem[]>("/quote-parts/api/quote-parts/", "GET");
  }
  public get_quote_parts_module_enabled(): Observable<EnableValue> {
    return this.makeRequest<EnableValue>("/quote-parts/api/quote-parts-module-enabled/", "GET");
  }
  public get_associated_perimeter_types(siteId: number): Observable<PerimeterTypes> {
    return this.makeRequest<PerimeterTypes>(`/quote-parts/api/associated-perimeter-types/${siteId}`, "GET");
  }
  public get_associated_perimeters(siteId: number): Observable<AssociatedPerimeters> {
    return this.makeRequest<AssociatedPerimeters>(`/quote-parts/api/associated-perimeters/${siteId}`, "GET");
  }
  public get_default_quote_part_by_perimeter(perimeterId: number): Observable<QuotePartIds> {
    return this.makeRequest<QuotePartIds>(`/quote-parts/api/default-quote-parts-by-perimeter/${perimeterId}`, "GET");
  }
  public post_quote_parts_create(payload: QuotePartCreatePayload): Observable<QuotePartCreateResponse> {
    return this.makeRequest<QuotePartCreateResponse>(`/quote-parts/api/quote-parts/`, "POST", payload);
  }
  public get_quote_parts_by_site(siteId: number): Observable<QuotePartItem[]> {
    return this.makeRequest<QuotePartItem[]>(`/quote-parts/api/quote-parts-by-perimeter/${siteId}`, "GET");
  }
  public get_quote_part_by_id(id: number): Observable<QuotePartItem> {
    return this.makeRequest<QuotePartItem>(`/quote-parts/api/quote-parts/${id}`, "GET");
  }
  public delete_quote_part(id: number): Observable<QuotePartDeleteResponse> {
    return this.makeRequest<QuotePartDeleteResponse>(`/quote-parts/api/quote-parts/${id}`, "DELETE");
  }
  public patch_quote_parts_update(id: number, payload: QuotePartUpdatePayload): Observable<QuotePartUpdateResponse> {
    return this.makeRequest<QuotePartUpdateResponse>(`/quote-parts/api/quote-parts/${id}/`, "PATCH", payload);
  }
  public investments_create_complementary(id: number, payload: any): Observable<any> {
    return this.makeRequest<any>(`/investments/api/investments/${id}/create-complementary/`, 'POST', payload);
  }

  public post_other_investment_quote_part(investmentId: number, payload: QuotePartUpdatePayload) {
    return this.makeRequest(`/investments/api/investments/${investmentId}/set-quote-part-other/`, "POST", payload);
  }

  private makeRequest<T>(
    endpoint: string,
    method: string,
    body?: any,
    params?: any,
    headers: HttpHeaders = new HttpHeaders().set("Content-Type", "application/json"),
    responseType?: any,
  ): Observable<T> {
    if (body instanceof FormData) {
      headers = headers.delete("Content-Type");
    }
    return <Observable<T>>this.http.request(method, `${environment.backendHost}${endpoint}`, {
      ...(params ? { params } : {}),
      ...(body ? { body } : {}),
      headers: headers,
       ...(responseType ? { responseType } : {}),
    });
  }

  private followPagination<T>(
    endpoint: string,
    params?: any,
    waitAllPages: boolean = false,
    pageSize: number = 10000,
    postParams: boolean = false
  ): Observable<T[]> {
    const method = postParams ? "POST" : "GET";
    let body = null;
    const parameters = postParams
      ? { size: pageSize, ...(params?.fields ? { fields: params.fields } : {}) }
      : { ...params, size: pageSize };
    let headers = new HttpHeaders();

    if (postParams) {
      headers = headers.set("Content-Type", "application/x-www-form-urlencoded");
      const param_helper = new URLSearchParams();
      for (const key in params) {
        if (key) {
          param_helper.set(key, params[key]);
        }
      }
      body = param_helper.toString();
    }

    const request = this.makeRequest<Pagination<T>>(endpoint, method, body, parameters, headers).pipe(
      // Recursively call the next page
      expand(({ next }) => (next ? <Observable<Pagination<T>>>this.http.request(method, next, {
        ...(body ? { body } : {}),
        headers: headers
      }) : empty())),
      // Emit the current page of results in an array
      concatMap(({ results }) => [results])
    );

    // Prevent the stream from emitting at each page
    if (waitAllPages) {
      return request.pipe(
        // Spread the array of results of the page
        concatMap(page => page),
        // Collect every sub-streams and emit them as array
        toArray()
      );
    }

    return request;
  }

  private apiGetList<T>(endpoint: string, params?: any, postParams: boolean = false): Observable<T[]> {
    const method = postParams ? "POST" : "GET";
    let body = null;
    const parameters = postParams ? {} : { ...params };
    let headers = new HttpHeaders();

    if (postParams) {
      headers = headers.set("Content-Type", "application/x-www-form-urlencoded");
      const param_helper = new URLSearchParams();
      for (const key in params) {
        if (key) {
          param_helper.set(key, params[key]);
        }
      }
      body = param_helper.toString();
    }

    return this.makeRequest<T[]>(endpoint, method, body, parameters, headers);
  }

  public get_initiative_responsibles(): Observable<{ responsibles: string[] }> {
    return this.makeRequest<{ responsibles: string[] }>("/initiatives/api/initiatives/responsibles/", "GET");
  }

  public delete_perimeter_by_id(id: number): Observable<void> {
    return this.makeRequest<void>(`/structures/api/perimeters/${id}/`, "DELETE");
  }

  // Maintenance API methods
  public create_asset_maintenance(payload: KPI.AssetMaintenanceCreatePayload): Observable<KPI.AssetMaintenance> {
    return this.makeRequest<KPI.AssetMaintenance>("/assets/api/maintenance/", "POST", JSON.stringify(payload));
  }

  public update_asset_maintenance(
    id: number,
    payload: KPI.AssetMaintenanceUpdatePayload
  ): Observable<KPI.AssetMaintenance> {
    return this.makeRequest<KPI.AssetMaintenance>(`/assets/api/maintenance/${id}/`, "PATCH", JSON.stringify(payload));
  }

  public patch_asset_maintenance(
    id: number,
    payload: Partial<KPI.AssetMaintenanceUpdatePayload>
  ): Observable<KPI.AssetMaintenance> {
    return this.makeRequest<KPI.AssetMaintenance>(`/assets/api/maintenance/${id}/`, "PATCH", JSON.stringify(payload));
  }

  // Compliance API methods
  public create_asset_compliance(payload: KPI.AssetComplianceCreatePayload): Observable<KPI.AssetCompliance> {
    return this.makeRequest<KPI.AssetCompliance>("/assets/api/compliance/", "POST", JSON.stringify(payload));
  }

  public update_asset_compliance(
    id: number,
    payload: KPI.AssetComplianceUpdatePayload
  ): Observable<KPI.AssetCompliance> {
    return this.makeRequest<KPI.AssetCompliance>(`/assets/api/compliance/${id}/`, "PUT", JSON.stringify(payload));
  }

  public patch_asset_compliance(
    id: number,
    payload: Partial<KPI.AssetComplianceUpdatePayload>
  ): Observable<KPI.AssetCompliance> {
    return this.makeRequest<KPI.AssetCompliance>(`/assets/api/compliance/${id}/`, "PATCH", JSON.stringify(payload));
  }

  // Reservations API methods
  public bulk_create_reservations(
    payload: KPI.ReservationsBulkCreatePayload
  ): Observable<KPI.ReservationsBulkCreateResponse> {
    return this.makeRequest<KPI.ReservationsBulkCreateResponse>(
      "/assets/api/reservations/bulk-create/",
      "POST",
      JSON.stringify(payload)
    );
  }

  public update_reservation(id: number, payload: KPI.ReservationUpdatePayload): Observable<KPI.Reservation> {
    return this.makeRequest<KPI.Reservation>(`/assets/api/reservations/${id}/`, "PUT", JSON.stringify(payload));
  }

  public patch_reservation(id: number, payload: Partial<KPI.ReservationUpdatePayload>): Observable<KPI.Reservation> {
    return this.makeRequest<KPI.Reservation>(`/assets/api/reservations/${id}/`, "PATCH", JSON.stringify(payload));
  }

  public delete_reservation(id: number): Observable<void> {
    return this.makeRequest<void>(`/assets/api/reservations/${id}/`, "DELETE");
  }

  public getAlertsTable(perimeterId?: number, reservationStatus?: string): Observable<any> {
    let url = '/assets/api/alerts-table/';
    const params: string[] = [];

    if (perimeterId) {
      params.push(`perimeter=${perimeterId}`);
    }

    if (reservationStatus) {
      params.push(`reservation_status=${reservationStatus}`);
    }

    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    return this.makeRequest<any>(url, 'GET');
  }

  public toggle_asset_maintenance(assetId: number): Observable<any> {
    return this.makeRequest<any>(`/assets/api/assets/${assetId}/toggle-maintenance/`, "POST");
  }

  public toggle_asset_compliance(assetId: number): Observable<any> {
    return this.makeRequest<any>(`/assets/api/assets/${assetId}/toggle-compliance/`, "POST");
  }

  public list_control_office_reports(params?: any): Observable<KPI.ControlOfficeReport[]> {
    return this.makeRequest<KPI.ControlOfficeReport[]>(`/assets/api/control-office-reports/`, "GET", null, params);
  }

  public get_control_office_report(id: number): Observable<KPI.ControlOfficeReport> {
    return this.makeRequest<KPI.ControlOfficeReport>(`/assets/api/control-office-reports/${id}/`, "GET");
  }

  public create_control_office_report(payload: KPI.ControlOfficeReportCreatePayload): Observable<KPI.ControlOfficeReport> {
    return this.makeRequest<KPI.ControlOfficeReport>(`/assets/api/control-office-reports/`, "POST", JSON.stringify(payload));
  }

  public update_control_office_report(id: number, payload: KPI.ControlOfficeReportUpdatePayload): Observable<KPI.ControlOfficeReport> {
    return this.makeRequest<KPI.ControlOfficeReport>(`/assets/api/control-office-reports/${id}/`, "PUT", JSON.stringify(payload));
  }

  public patch_control_office_report(id: number, payload: Partial<KPI.ControlOfficeReportUpdatePayload>): Observable<KPI.ControlOfficeReport> {
    return this.makeRequest<KPI.ControlOfficeReport>(`/assets/api/control-office-reports/${id}/`, "PATCH", JSON.stringify(payload));
  }

  public delete_control_office_report(id: number): Observable<void> {
    return this.makeRequest<void>(`/assets/api/control-office-reports/${id}/`, "DELETE");
  }

  public get_report_reservations(id: number, params?: any): Observable<KPI.Reservation[]> {
    return this.makeRequest<KPI.Reservation[]>(`/assets/api/control-office-reports/${id}/reservations/`, "GET", null, params);
  }

  public get_report_reservations_stats(id: number): Observable<KPI.ReportReservationsStats> {
    return this.makeRequest<KPI.ReportReservationsStats>(`/assets/api/control-office-reports/${id}/reservations/stats/`, "GET");
  }
}
